export type LanguageFaq = { question: string; answer: string };

export type LanguagePage = {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  script: string;
  regions: string[];
  seoTitle: string;
  seoDescription: string;
  answer: string;
  body: string[];
  faqs: LanguageFaq[];
  related: string[];
  rtl?: boolean;
};

export const CAPTIONS_PATH = "/captions";

export const languagePath = (slug: string) => `${CAPTIONS_PATH}/${slug}`;

const AUTO_CLIP = "/features/auto-clip";
const AI_CAPTIONS = "/features/ai-captions";
const VIDEO_EDITOR = "/features/video-editor";
const CAPTION_STYLES = "/caption-styles";
const PODCASTS = "/use-cases/podcast-clipping";
const CLIPPERS = "/use-cases/clippers";
const STREAMERS = "/use-cases/streamers";
const AGENCIES = "/use-cases/agencies";

export const LANGUAGE_PAGES: Record<string, LanguagePage> = {
  english: {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "GB",
    script: "Latin",
    regions: ["United States", "United Kingdom", "Canada", "Australia", "India", "Nigeria"],
    seoTitle: "English Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes English speech from podcasts, streams and interviews, then burns in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes English speech and adds animated, word-timed captions to short clips cut from podcasts, streams and interviews. It is built for clippers and editors who post English content to TikTok, Reels and Shorts every day.",
    body: [
      "English is the default language of the clipping economy. The biggest podcast, streamer and interview channels record in it, and the clips that circulate on TikTok, Instagram Reels and YouTube Shorts are overwhelmingly captioned in it, because most viewers watch with the sound off and read the caption instead of listening to the voice.",
      "English captions are mostly forgiving, but accents, slang and fast crosstalk still trip up transcription. Names of guests, brands and games are the usual misses, and contractions like gonna and wanna come back in different spellings depending on the speaker. Crayo lets you edit any word in the caption list, so fixing a name once takes a few seconds and the timing of the line stays put.",
      "A practical tip for English clips: keep one to three words on screen at a time for a punchy word-by-word style, or a full short sentence for a calmer two-line look, and pick the one that matches how fast the guest talks. A rapid streamer rant reads better with single-word pops, while a slow interview answer looks better as a sentence.",
    ],
    faqs: [
      {
        question: "Does Crayo caption English videos?",
        answer:
          "Yes. English is the first language in Crayo's language list. AutoClip transcribes the speech, finds the strongest moments and burns in animated captions that follow each word.",
      },
      {
        question: "Can I edit the English transcript?",
        answer:
          "Yes. Every caption line appears in an editable list in the editor. Click any word to change the spelling, fix a name or remove a filler, and the line keeps its original timing.",
      },
      {
        question: "Which caption styles work for English?",
        answer:
          "All 32 caption styles in Crayo were designed with English clips in mind, from single-word pop styles to two-line karaoke highlights. Pick one on the caption styles page and preview it on your clip before exporting.",
      },
      {
        question: "Does Crayo translate English captions?",
        answer:
          "Yes. The caption list has a Translate button that sends your English lines to Crayo's translator and swaps the text into another language from the list, such as Spanish or Portuguese, while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CLIPPERS],
  },
  afrikaans: {
    code: "af",
    name: "Afrikaans",
    nativeName: "Afrikaans",
    flag: "ZA",
    script: "Latin",
    regions: ["South Africa", "Namibia"],
    seoTitle: "Afrikaans Auto Captions and Subtitles for Clips",
    seoDescription:
      "Transcribe Afrikaans podcasts, streams and interviews with Crayo and burn in animated captions for TikTok, Reels and Shorts in one upload.",
    answer:
      "Crayo transcribes Afrikaans speech and adds animated captions to short clips from podcasts, streams and interviews. It suits South African and Namibian creators who want vertical clips ready for TikTok, Reels and Shorts without typing subtitles by hand.",
    body: [
      "Afrikaans content lives mostly in South Africa and Namibia, where TikTok and YouTube Shorts are the main places short clips travel and WhatsApp forwards do the rest. Afrikaans podcasts, radio-style interviews and gaming streams produce long recordings that are a natural fit for clipping, and most of that audience watches muted on mobile data.",
      "The language is written in the Latin alphabet with a handful of diacritics, such as the diaeresis in words like reën and the circumflex in sê. Transcription handles the base words well, but the diacritics are worth checking in the caption list, and code switching into English mid-sentence is very common, so expect a few English words in the transcript that you may want to leave as is.",
      "A practical tip: Afrikaans sentences often run long, so pick a caption style that shows a few words at a time instead of a full sentence. That keeps the text large and readable on a phone and avoids the two-line crowding that longer Germanic sentences cause.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Afrikaans videos?",
        answer:
          "Yes. Afrikaans is in Crayo's language list. Select it before running AutoClip so the transcription does not fall back to auto detection on clips that mix Afrikaans and English.",
      },
      {
        question: "Can I edit the Afrikaans transcript?",
        answer:
          "Yes. Captions are listed line by line in the editor and every word is editable. Fix a diacritic or a place name and the caption keeps its timing.",
      },
      {
        question: "Which caption styles work for Afrikaans?",
        answer:
          "Afrikaans uses the Latin alphabet, so all 32 caption styles apply without surprises. Shorter word-by-word styles suit the long sentences best.",
      },
      {
        question: "Does Crayo translate Afrikaans captions?",
        answer:
          "Yes. Use the Translate button in the caption list to turn Afrikaans lines into English or another listed language. Timing is preserved, so the clip does not need to be re-cut.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  arabic: {
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    flag: "SA",
    script: "Arabic",
    regions: ["Saudi Arabia", "Egypt", "United Arab Emirates", "Morocco", "Iraq"],
    rtl: true,
    seoTitle: "Arabic Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Arabic speech from podcasts, streams and interviews and adds animated captions to vertical clips for TikTok, Reels, Shorts and Snapchat.",
    answer:
      "Crayo transcribes Arabic speech and adds animated captions to short clips cut from podcasts, streams and interviews. It is made for clippers serving Gulf, Egyptian and Maghreb audiences on TikTok, Reels, Shorts and Snapchat.",
    body: [
      "Arabic is one of the largest short-form audiences in the world. Saudi Arabia and Egypt drive huge TikTok and Snapchat watch time, the Gulf is a major YouTube Shorts market, and Arabic podcast networks and streamer communities produce hours of long-form talk every week that clippers turn into vertical cuts.",
      "Arabic is written right to left in a cursive script where letters change shape depending on their position, and most text is written without the short vowel marks. Dialects also differ a lot: Egyptian, Levantine, Gulf and Maghrebi speech are all transcribed as Arabic, but spellings of dialect words vary, so check the caption list for words you would write differently. Crayo does not add vowel marks, which is how most on-screen Arabic reads anyway.",
      "A practical tip for Arabic clips: preview the caption style on the actual clip before you export, since the right-to-left script and the cursive joins look different from style to style, and keep each caption to a few words so the text stays large and the letter forms stay clear on a phone screen.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Arabic videos?",
        answer:
          "Yes. Arabic is in Crayo's language list and covers the spoken dialects as one language. Pick Arabic in the language selector before running AutoClip for the most accurate transcript.",
      },
      {
        question: "Can I edit the Arabic transcript?",
        answer:
          "Yes. Each caption is an editable line in the editor, so you can correct a dialect spelling, a name or a religious phrase and keep the timing of the line.",
      },
      {
        question: "Which caption styles work for Arabic script?",
        answer:
          "All 32 caption styles can be applied to Arabic captions. Because the fonts differ between styles, preview the style on your clip and choose one where the cursive letter forms read cleanly at phone size.",
      },
      {
        question: "Does Crayo translate Arabic captions?",
        answer:
          "Yes. The Translate button in the caption list converts Arabic lines into English or any other listed language while keeping the original timing, which is useful for reaching viewers outside the region.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, CLIPPERS, PODCASTS],
  },
  armenian: {
    code: "hy",
    name: "Armenian",
    nativeName: "Հայերեն",
    flag: "AM",
    script: "Armenian",
    regions: ["Armenia", "Armenian diaspora in Russia, the United States and France"],
    seoTitle: "Armenian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Armenian interviews, podcasts and streams with Crayo and export vertical clips with animated captions for TikTok, Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Armenian speech and adds animated captions to short clips from podcasts, streams and interviews. It helps Armenian creators and diaspora channels post captioned clips to TikTok, Reels and Shorts.",
    body: [
      "Armenian short-form content serves a small home market and a large diaspora spread across Russia, the United States, France and Lebanon. Interview channels, political talk shows and music podcasts in Yerevan produce long episodes, and diaspora viewers who understand spoken Armenian better than they read it still rely on captions when watching muted.",
      "Armenian has its own alphabet with 39 letters and distinct uppercase and lowercase forms, and Eastern and Western Armenian differ in pronunciation and some spelling. Crayo transcribes the speech into the Armenian alphabet; if your guest speaks Western Armenian you may want to scan the caption list for spellings you would write differently.",
      "A practical tip: Armenian words can be long and the uppercase forms are tall, so a two-line sentence style can get crowded. Styles that show a few words at a time keep the text readable, and previewing the style on the clip before export shows you how the Armenian letters render in that style's font.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Armenian videos?",
        answer:
          "Yes. Armenian is in Crayo's language list. Select it before you run AutoClip so the speech is transcribed as Armenian rather than auto detected.",
      },
      {
        question: "Can I edit the Armenian transcript?",
        answer:
          "Yes. The editor lists every caption line and lets you change any word in place. Edits do not shift the timing of the line.",
      },
      {
        question: "Which caption styles work for the Armenian alphabet?",
        answer:
          "Every one of the 32 caption styles can be applied. Fonts vary by style, so preview a few on an Armenian clip and keep the one where the letters look sharpest.",
      },
      {
        question: "Does Crayo translate Armenian captions?",
        answer:
          "Yes. From the caption list, Translate turns Armenian lines into English, Russian or any other listed language while the timing stays the same.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  belarusian: {
    code: "be",
    name: "Belarusian",
    nativeName: "Беларуская",
    flag: "BY",
    script: "Cyrillic",
    regions: ["Belarus", "Belarusian communities in Poland and Lithuania"],
    seoTitle: "Belarusian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Belarusian speech and burns animated captions into vertical clips from podcasts, interviews and streams for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Belarusian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Belarusian-language creators and media teams, at home or abroad, who post to TikTok, YouTube Shorts and Instagram.",
    body: [
      "Belarusian-language media has grown mostly outside Belarus, with newsrooms, podcasts and YouTube channels run from Warsaw and Vilnius. Their audiences follow on YouTube and Telegram first, and short captioned clips on TikTok and Shorts are how the longer interviews reach people who would never sit through a full episode.",
      "Belarusian is written in Cyrillic with a few letters you will not see in Russian, such as ў and і, and speech frequently mixes Belarusian with Russian. Crayo transcribes what it hears as Belarusian, so a guest switching into Russian can produce lines you want to tidy in the caption list. The language also uses an apostrophe inside words, which transcription normally keeps.",
      "A practical tip: choose Belarusian explicitly in the language picker rather than leaving it on auto detect, because Belarusian and Russian are close enough that mixed recordings can be detected as Russian. Then skim the first minute of captions to confirm the spellings look right before clipping the rest.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Belarusian videos?",
        answer:
          "Yes. Belarusian is in the language list. Choose it before running AutoClip so the transcript comes back in Belarusian spelling rather than Russian.",
      },
      {
        question: "Can I edit the Belarusian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so you can fix a mixed Russian word or a surname and keep the timing.",
      },
      {
        question: "Which caption styles work for Cyrillic?",
        answer:
          "All 32 caption styles can be applied to Belarusian captions. Preview the style on a real clip to check how its font renders Cyrillic before you export.",
      },
      {
        question: "Does Crayo translate Belarusian captions?",
        answer:
          "Yes. The Translate button in the caption list sends the lines to Crayo's translator and returns them in English, Polish or another listed language with the timing intact.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CLIPPERS, CAPTION_STYLES],
  },
  bengali: {
    code: "bn",
    name: "Bengali",
    nativeName: "বাংলা",
    flag: "BD",
    script: "Bengali",
    regions: ["Bangladesh", "West Bengal, India"],
    seoTitle: "Bengali Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Bengali podcasts, interviews and streams with Crayo and export vertical clips with animated captions for YouTube Shorts, Reels and TikTok.",
    answer:
      "Crayo transcribes Bengali speech and adds animated captions to short clips from podcasts, interviews and streams. It is for creators in Bangladesh and West Bengal who post to YouTube Shorts, Facebook Reels and Instagram.",
    body: [
      "Bengali is spoken by well over two hundred million people across Bangladesh and the Indian state of West Bengal, and the short-form audience is mostly on YouTube Shorts and Facebook, with Instagram Reels growing fast in Kolkata and Dhaka. Bengali talk shows, interview podcasts and gaming streams give clippers plenty of long-form source material.",
      "Bengali uses its own abugida script, where consonants carry an inherent vowel and vowel signs attach around them, and conjunct consonants stack into single glyphs. That makes captions dense, so each on-screen caption should carry fewer words than an English one. Transcription writes standard Bengali; regional pronunciations in Sylhet or Chittagong are still transcribed as Bengali but may read slightly differently from how the speaker would spell them.",
      "A practical tip: preview your chosen caption style on a Bengali clip before exporting, since fonts differ across the 32 styles and conjunct glyphs need a clean font to stay legible at phone size. Bigger text with two or three words per caption works better than a full sentence.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Bengali videos?",
        answer:
          "Yes. Bengali is in Crayo's language list. Pick it before running AutoClip so the speech is transcribed in Bengali script.",
      },
      {
        question: "Can I edit the Bengali transcript?",
        answer:
          "Yes. The editor shows every caption line and lets you edit any word in place. The line keeps its timing when the text changes.",
      },
      {
        question: "Which caption styles work for Bengali script?",
        answer:
          "All 32 styles can be applied to Bengali captions. Because styles use different fonts, preview a few on the clip and pick one where the conjunct letters render clearly.",
      },
      {
        question: "Does Crayo translate Bengali captions?",
        answer:
          "Yes. The Translate button in the caption list converts Bengali lines into English, Hindi or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CLIPPERS],
  },
  bosnian: {
    code: "bs",
    name: "Bosnian",
    nativeName: "Bosanski",
    flag: "BA",
    script: "Latin",
    regions: ["Bosnia and Herzegovina", "Bosnian diaspora in Germany, Austria and Scandinavia"],
    seoTitle: "Bosnian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Bosnian podcasts, streams and interviews and adds animated captions to vertical clips ready for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Bosnian speech and adds animated captions to short clips from podcasts, streams and interviews. It serves creators in Bosnia and Herzegovina and the large diaspora in Germany, Austria and Scandinavia posting to TikTok, Reels and Shorts.",
    body: [
      "Bosnian short-form content reaches a home audience in Sarajevo, Tuzla and Mostar and a diaspora that is larger than many people expect, concentrated in Germany, Austria, Sweden and the United States. Podcast interviews and streamer clips travel across the whole Bosnian, Croatian and Serbian speaking region, since the languages are mutually intelligible, which makes captioned clips especially shareable.",
      "Bosnian is written in the Latin alphabet with diacritics on č, ć, đ, š and ž. Those letters matter for meaning and for how native a caption looks, so after transcription it is worth scanning the caption list for any word that came back without its diacritic. Crayo transcribes the speech as Bosnian when you pick it; leaving the picker on auto detect can return Croatian or Serbian spellings instead.",
      "A practical tip: regional podcast guests often switch between Bosnian, Croatian and Serbian forms within one conversation. Decide on one spelling convention for the clip and apply it in the caption list, which keeps the captions consistent even when the speakers are not.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Bosnian videos?",
        answer:
          "Yes. Bosnian is a separate entry in Crayo's language list. Select it before running AutoClip so the transcript uses Bosnian forms.",
      },
      {
        question: "Can I edit the Bosnian transcript?",
        answer:
          "Yes. Each caption line is editable in the editor, so adding a missing diacritic or correcting a name takes seconds and the timing stays as it was.",
      },
      {
        question: "Which caption styles work for Bosnian?",
        answer:
          "All 32 caption styles apply, since Bosnian uses the Latin alphabet. Preview your pick on the clip to confirm the accented letters look right in that style's font.",
      },
      {
        question: "Does Crayo translate Bosnian captions?",
        answer:
          "Yes. The Translate button in the caption list turns Bosnian lines into German, English or any other listed language without changing the line timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, STREAMERS],
  },
  bulgarian: {
    code: "bg",
    name: "Bulgarian",
    nativeName: "Български",
    flag: "BG",
    script: "Cyrillic",
    regions: ["Bulgaria"],
    seoTitle: "Bulgarian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Bulgarian podcasts, interviews and streams with Crayo and export vertical clips with animated Cyrillic captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Bulgarian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Bulgarian podcasters, streamers and the clippers who cut their content for TikTok, Reels and YouTube Shorts.",
    body: [
      "Bulgaria has a lively podcast and YouTube interview scene in Sofia and Plovdiv, and a streamer community that grew out of the country's strong esports history. The short-form audience is on TikTok and Instagram Reels, with YouTube Shorts catching up, and most clips are watched on phones without sound, so captions decide whether a clip gets watched at all.",
      "Bulgarian is written in Cyrillic and, unlike Russian, has no hard sign at the end of words and uses the letter ъ as a full vowel, so a transcript that looks Russian-flavoured is a sign the wrong language was picked. Crayo transcribes Bulgarian when you select it; the main things to check in the caption list are proper names and the occasional English loanword from tech and gaming talk.",
      "A practical tip for Bulgarian clips: Cyrillic letters are wider on average than Latin ones, so a caption style that shows two or three words at a time keeps the text large enough to read at a glance. Preview the style before exporting to see how its font handles Cyrillic.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Bulgarian videos?",
        answer:
          "Yes. Bulgarian is in Crayo's language list. Select it before running AutoClip for a transcript in standard Bulgarian Cyrillic.",
      },
      {
        question: "Can I edit the Bulgarian transcript?",
        answer:
          "Yes. The editor lists every caption and lets you edit words in place. Correcting a name or a loanword keeps the line's timing.",
      },
      {
        question: "Which caption styles work for Cyrillic?",
        answer:
          "All 32 caption styles can be applied to Bulgarian captions. Fonts differ by style, so preview a couple on a real clip and keep the one where Cyrillic reads cleanly.",
      },
      {
        question: "Does Crayo translate Bulgarian captions?",
        answer:
          "Yes. Use the Translate button in the caption list to turn Bulgarian lines into English or another listed language while the timing stays unchanged.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, STREAMERS, PODCASTS],
  },
  catalan: {
    code: "ca",
    name: "Catalan",
    nativeName: "Català",
    flag: "AD",
    script: "Latin",
    regions: ["Catalonia", "Valencia", "Balearic Islands", "Andorra"],
    seoTitle: "Catalan Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Catalan speech from podcasts, streams and interviews and burns animated captions into vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Catalan speech and adds animated captions to short clips from podcasts, streams and interviews. It is for creators in Catalonia, Valencia, the Balearic Islands and Andorra who want captioned clips without relying on Spanish transcription.",
    body: [
      "Catalan creators have built a real short-form scene around Barcelona and Valencia, with podcasts, Twitch streamers and public-media interview shows producing long recordings in Catalan every week. Their clips live on TikTok, Instagram Reels and YouTube Shorts, and captions matter twice over: for muted viewing, and because many viewers in the region read Catalan more comfortably than they hear fast speech.",
      "Catalan uses the Latin alphabet with accents in both directions, the cedilla in ç, the middle dot in words like col·legi and frequent apostrophes and hyphens in pronoun clusters. Transcription handles the words, but the middle dot and the direction of accents are worth a glance in the caption list. Speakers also drift into Spanish, and choosing Catalan in the language picker keeps the transcript from being detected as Spanish.",
      "A practical tip: Catalan clips often come from multi-speaker shows, so pick a caption style with a strong highlight on the active word to help viewers follow who is talking, and keep captions to a few words so the apostrophe-heavy phrases do not crowd the frame.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Catalan videos?",
        answer:
          "Yes. Catalan has its own entry in Crayo's language list, separate from Spanish. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Catalan transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a missing middle dot or accent can be fixed in place and the timing is kept.",
      },
      {
        question: "Which caption styles work for Catalan?",
        answer:
          "All 32 caption styles apply to Catalan, which uses the Latin alphabet. Preview the style on the clip to confirm accents and ç render well in that font.",
      },
      {
        question: "Does Crayo translate Catalan captions?",
        answer:
          "Yes. The Translate button in the caption list converts Catalan lines into Spanish, English or another listed language and keeps each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, PODCASTS],
  },
  chinese: {
    code: "zh",
    name: "Chinese",
    nativeName: "中文",
    flag: "CN",
    script: "Han (Chinese characters)",
    regions: ["China", "Taiwan", "Singapore", "Malaysia", "Chinese diaspora"],
    seoTitle: "Chinese Auto Captions and Subtitles for Short Clips",
    seoDescription:
      "Transcribe Mandarin Chinese podcasts and streams with Crayo and export vertical clips with animated character captions for Shorts, Reels and TikTok.",
    answer:
      "Crayo transcribes Chinese speech and adds animated captions to short clips cut from podcasts, streams and interviews. It is for Mandarin-speaking creators and clippers reaching audiences in Taiwan, Singapore, Malaysia and the global diaspora on YouTube Shorts, Instagram and TikTok.",
    body: [
      "Chinese-language short-form content outside mainland China is concentrated on YouTube Shorts, Instagram Reels and TikTok, with Taiwan, Singapore, Malaysia and overseas communities as the main audiences. Mandarin podcasts and interview channels have exploded in the last few years, and burned-in captions are a long-standing norm in Chinese video, so viewers expect them on every clip.",
      "Chinese is written in characters without spaces between words, so line breaking has to happen at phrase boundaries rather than at spaces, and a character carries more meaning than a Latin letter, which means captions should hold fewer characters than an English caption holds letters. Transcription returns characters; whether they are simplified or traditional depends on the model output, so Taiwanese creators should check the caption list and edit lines that need traditional forms.",
      "A practical tip: Mandarin speakers vary a lot in pace, and tone-heavy speech is hard to follow muted, so choose a caption style that highlights the current phrase and keep the characters large. Preview the style on the clip before exporting, because the fonts differ between styles and characters need a clean, heavy weight to read on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Chinese videos?",
        answer:
          "Yes. Chinese is in Crayo's language list and covers spoken Mandarin. Select it before running AutoClip so the audio is transcribed into Chinese characters.",
      },
      {
        question: "Can I edit the Chinese transcript?",
        answer:
          "Yes. Captions are listed line by line in the editor and any text can be edited in place, including switching a line to traditional characters. Timing stays unchanged.",
      },
      {
        question: "Which caption styles work for Chinese characters?",
        answer:
          "All 32 caption styles can be applied to Chinese captions. Fonts vary by style, so preview on a real clip and keep one where the characters render sharply at phone size.",
      },
      {
        question: "Does Crayo translate Chinese captions?",
        answer:
          "Yes. The Translate button in the caption list sends Chinese lines to Crayo's translator and returns them in English or another listed language with the same timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CLIPPERS],
  },
  croatian: {
    code: "hr",
    name: "Croatian",
    nativeName: "Hrvatski",
    flag: "HR",
    script: "Latin",
    regions: ["Croatia", "Croatian diaspora in Germany, Austria and Australia"],
    seoTitle: "Croatian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Croatian podcasts, streams and interviews and adds animated captions to vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Croatian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Croatian podcasters, streamers and clippers posting to TikTok, Reels and Shorts at home and across the diaspora.",
    body: [
      "Croatia punches above its size in podcasts and streaming, with Zagreb interview shows, football talk and a busy Twitch scene whose clips circulate across the whole former Yugoslav region. The audience watches on TikTok and Instagram Reels, and a captioned clip travels further because Bosnian and Serbian speakers read it just as easily.",
      "Croatian is written in the Latin alphabet with the diacritics č, ć, đ, š and ž, and the difference between č and ć is a frequent source of caption errors. Transcription gets most of it right, but names, places and the ije and je spellings that distinguish Croatian from neighbouring standards are worth a check in the caption list. Pick Croatian explicitly so the transcript does not come back in Serbian or Bosnian forms.",
      "A practical tip: Croatian compound phrases can be long, so a caption style that shows two or three words at a time keeps text large and readable. For football and gaming clips with shouting and crosstalk, skim the captions for the loudest moments, which are where transcription is most likely to need a small edit.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Croatian videos?",
        answer:
          "Yes. Croatian is in Crayo's language list as its own entry. Choose it before running AutoClip to get Croatian spellings.",
      },
      {
        question: "Can I edit the Croatian transcript?",
        answer:
          "Yes. Every caption line in the editor is editable word by word, so fixing a č or ć or a player's name keeps the timing intact.",
      },
      {
        question: "Which caption styles work for Croatian?",
        answer:
          "All 32 caption styles apply since Croatian uses the Latin alphabet. Preview the style on your clip to confirm the accented letters render properly.",
      },
      {
        question: "Does Crayo translate Croatian captions?",
        answer:
          "Yes. The Translate button in the caption list turns Croatian lines into English, German or any other listed language with the line timing preserved.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, PODCASTS],
  },
  czech: {
    code: "cs",
    name: "Czech",
    nativeName: "Čeština",
    flag: "CZ",
    script: "Latin",
    regions: ["Czech Republic"],
    seoTitle: "Czech Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Czech podcasts, streams and interviews with Crayo and burn in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Czech speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Czech podcasters, streamers and the clippers who cut their shows into TikTok, Reels and Shorts.",
    body: [
      "The Czech Republic has one of the strongest podcast cultures in Central Europe, with interview shows, true crime and business podcasts recorded in Prague and Brno, and a streamer scene that regularly tops local Twitch charts. Short clips of those shows get their views on TikTok, Instagram Reels and YouTube Shorts, and Slovak viewers follow the same channels, so captioned clips reach both countries.",
      "Czech uses the Latin alphabet with a heavy load of diacritics: háček letters like č, ř, š, ž and ě, acute vowels and the ring in ů. The ř sound in particular is often mistranscribed, and long proper names come back inconsistently. Crayo transcribes the speech as Czech when selected; a quick pass through the caption list usually catches the few diacritics that need fixing.",
      "A practical tip for Czech clips: diacritics sit above and below the letters, so leave enough caption size and avoid cramped two-line styles. A style that shows a short phrase at a time, with a highlight on the active word, keeps Czech text legible on a phone and makes fast, dry Czech humour land in the clip.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Czech videos?",
        answer:
          "Yes. Czech is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Czech rather than detected as Slovak.",
      },
      {
        question: "Can I edit the Czech transcript?",
        answer:
          "Yes. The editor lists every caption and lets you correct any word in place. Fixing a diacritic or a name does not change the timing of the line.",
      },
      {
        question: "Which caption styles work for Czech?",
        answer:
          "All 32 caption styles can be applied to Czech. Preview the style on your clip and pick one with a font that renders the háček and ring letters cleanly.",
      },
      {
        question: "Does Crayo translate Czech captions?",
        answer:
          "Yes. The Translate button in the caption list converts Czech lines into English, Slovak or another listed language while the timing stays the same.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, STREAMERS],
  },
  danish: {
    code: "da",
    name: "Danish",
    nativeName: "Dansk",
    flag: "DK",
    script: "Latin",
    regions: ["Denmark"],
    seoTitle: "Danish Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Danish speech from podcasts, streams and interviews and adds animated captions to vertical clips for TikTok, Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Danish speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Danish podcasters, streamers and clippers who want captioned vertical clips for TikTok, Reels and Shorts without manual subtitling.",
    body: [
      "Denmark is a podcast-heavy country where interview shows, comedy podcasts and sports talk fill the charts, and Danish streamers have a loyal audience on Twitch. Short clips of those shows spread on TikTok and Instagram Reels, and because Danish is famously hard to understand when spoken quickly, even native viewers appreciate captions on muted clips.",
      "Danish uses the Latin alphabet plus æ, ø and å, and spoken Danish swallows syllables so aggressively that transcription can struggle with casual, fast speech more than with the written-sounding delivery of a radio host. Crayo transcribes the audio as Danish when selected; numbers, place names and English loanwords are the lines most likely to need a touch in the caption list.",
      "A practical tip: for a relaxed Danish podcast conversation, a two-line sentence style reads naturally, but for a fast comedy exchange switch to a word-by-word style so the captions keep pace with the speaker. Either way, keep æ, ø and å large enough to be told apart at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Danish videos?",
        answer:
          "Yes. Danish is in Crayo's language list. Pick it before running AutoClip so the transcript comes back in Danish rather than being detected as Norwegian.",
      },
      {
        question: "Can I edit the Danish transcript?",
        answer:
          "Yes. Every caption line is editable in the editor. Fix a swallowed word or a name and the timing of the line is unchanged.",
      },
      {
        question: "Which caption styles work for Danish?",
        answer:
          "All 32 caption styles apply, since Danish uses the Latin alphabet with three extra letters. Preview a style on your clip to check how æ, ø and å look in its font.",
      },
      {
        question: "Does Crayo translate Danish captions?",
        answer:
          "Yes. The Translate button in the caption list turns Danish lines into English or any other listed language and keeps each line's timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  dutch: {
    code: "nl",
    name: "Dutch",
    nativeName: "Nederlands",
    flag: "NL",
    script: "Latin",
    regions: ["Netherlands", "Belgium (Flanders)", "Suriname"],
    seoTitle: "Dutch Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Dutch and Flemish podcasts, streams and interviews with Crayo and export vertical clips with animated captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Dutch speech and adds animated captions to short clips from podcasts, streams and interviews. It is for creators in the Netherlands and Flanders who post captioned clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Dutch-language short-form content serves the Netherlands and Flemish Belgium, two of the most TikTok-active markets in Europe, and Dutch podcasts routinely top the charts with multi-hour interview episodes. YouTube is the home of Dutch long-form talk, so clipping those episodes into vertical captioned cuts is a well-worn path for Dutch clippers.",
      "Dutch is written in the Latin alphabet with few diacritics but very long compound nouns and a lot of English borrowed into everyday speech. Flemish and Netherlands Dutch are transcribed as the same language, with occasional regional vocabulary that may need an edit. Crayo transcribes the speech as Dutch when selected, and English words inside Dutch sentences generally come through as English.",
      "A practical tip: long Dutch compounds do not break nicely across lines, so choose a caption style that shows one to three words at a time rather than a full sentence, and preview any clip with a long compound to make sure it fits inside the safe area on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Dutch videos?",
        answer:
          "Yes. Dutch is in Crayo's language list and covers both Netherlands Dutch and Flemish. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Dutch transcript?",
        answer:
          "Yes. The editor lists every caption line and lets you edit words in place, with the timing kept as it was.",
      },
      {
        question: "Which caption styles work for Dutch?",
        answer:
          "All 32 caption styles apply to Dutch. Styles that show a few words at a time handle long compound words best; preview before you export.",
      },
      {
        question: "Does Crayo translate Dutch captions?",
        answer:
          "Yes. The Translate button in the caption list converts Dutch lines into English, German or another listed language while keeping the timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CLIPPERS, CAPTION_STYLES],
  },
  estonian: {
    code: "et",
    name: "Estonian",
    nativeName: "Eesti",
    flag: "EE",
    script: "Latin",
    regions: ["Estonia"],
    seoTitle: "Estonian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Estonian podcasts, interviews and streams and burns in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Estonian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Estonian creators who want captioned vertical clips for TikTok, Reels and Shorts without typing subtitles themselves.",
    body: [
      "Estonia is small but extremely online, with a startup-driven podcast scene, public-media interview shows and a surprisingly active streamer community for a country of its size. Estonian clips travel on TikTok, Instagram Reels and YouTube Shorts, and Finnish viewers often follow along because the languages are related enough that captions make the content accessible to them.",
      "Estonian uses the Latin alphabet with õ, ä, ö and ü, and it is agglutinative, so a single word can carry what English says in four. Long inflected words and frequent English tech vocabulary are the main things to check after transcription. Crayo transcribes the speech as Estonian when you select it, and the õ letter, which is unique to Estonian among its neighbours, is worth a glance in the caption list.",
      "A practical tip: because Estonian words are long, a caption style that shows one or two words at a time keeps the text big, and a style with a solid background keeps the diacritics readable over busy footage. Preview your pick on the clip before exporting.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Estonian videos?",
        answer:
          "Yes. Estonian is in Crayo's language list. Select it before running AutoClip so the transcription is Estonian rather than auto detected as Finnish.",
      },
      {
        question: "Can I edit the Estonian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so an inflected ending or a name can be corrected without touching the timing.",
      },
      {
        question: "Which caption styles work for Estonian?",
        answer:
          "All 32 caption styles apply, as Estonian uses the Latin alphabet. Preview the style on your clip to check how õ, ä, ö and ü render in that font.",
      },
      {
        question: "Does Crayo translate Estonian captions?",
        answer:
          "Yes. The Translate button in the caption list turns Estonian lines into English, Finnish or another listed language while keeping the line timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  filipino: {
    code: "tl",
    name: "Filipino",
    nativeName: "Filipino",
    flag: "PH",
    script: "Latin",
    regions: ["Philippines", "Filipino diaspora in the Middle East, North America and Europe"],
    seoTitle: "Filipino Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Filipino and Tagalog podcasts, vlogs and streams with Crayo and export vertical clips with animated captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Filipino speech and adds animated captions to short clips from podcasts, streams and interviews. It is for creators in the Philippines and the overseas Filipino community who post Tagalog and Taglish clips to TikTok, Facebook Reels and YouTube Shorts.",
    body: [
      "The Philippines is one of the heaviest social video markets on earth, with enormous TikTok, Facebook and YouTube usage and a huge overseas community that keeps up with home through short clips. Filipino podcasts, vlog interviews and Facebook Live streams run for hours, and clippers cut them into vertical pieces that reach viewers from Manila to Dubai to Los Angeles.",
      "Filipino is written in the Latin alphabet and is usually spoken as Taglish, a continuous mix of Tagalog and English within the same sentence. Crayo transcribes the speech as Filipino when selected, and English words in the mix generally come through as English, which is how viewers expect to read them. Regional words from Cebuano or Ilocano speakers may need an edit in the caption list.",
      "A practical tip: Filipino conversations are fast and emotional, which is exactly what performs in short form, so pick a caption style with a strong highlight on the active word and keep two to four words on screen. Preview the clip to make sure the captions keep pace with the speaker.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Filipino videos?",
        answer:
          "Yes. Filipino is in Crayo's language list and handles Tagalog speech, including Taglish mixing with English. Choose it before running AutoClip.",
      },
      {
        question: "Can I edit the Filipino transcript?",
        answer:
          "Yes. The editor lists every caption and each word is editable in place, with the timing preserved after edits.",
      },
      {
        question: "Which caption styles work for Filipino?",
        answer:
          "All 32 caption styles apply, since Filipino uses the Latin alphabet. Bold word-by-word styles suit fast conversational clips best.",
      },
      {
        question: "Does Crayo translate Filipino captions?",
        answer:
          "Yes. The Translate button in the caption list converts Filipino lines into English or any other listed language and keeps the original timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, CAPTION_STYLES, PODCASTS],
  },
  finnish: {
    code: "fi",
    name: "Finnish",
    nativeName: "Suomi",
    flag: "FI",
    script: "Latin",
    regions: ["Finland"],
    seoTitle: "Finnish Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Finnish speech from podcasts, streams and interviews and adds animated captions to vertical clips for TikTok, Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Finnish speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Finnish podcasters, streamers and clippers who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Finland has a deep podcast and streaming culture, from long interview podcasts and comedy shows to one of the most committed gaming and esports communities in Europe. Finnish clips circulate on TikTok, Instagram Reels and YouTube Shorts, and because Finnish is rarely dubbed or spoken outside the country, captions are the only way a muted viewer follows a clip at all.",
      "Finnish is written in the Latin alphabet with ä and ö, and it is strongly agglutinative: a single word can be fifteen or more letters, and case endings carry much of the meaning. Transcription is generally accurate on clearly spoken Finnish, but spoken Finnish drops endings and merges words, so colloquial speech in the caption list may read more compactly than the written standard. Crayo transcribes the speech as Finnish when selected.",
      "A practical tip for Finnish clips: very long words break badly across two lines, so prefer a caption style that shows one or two words at a time, and scale the text so the longest words still fit within the phone safe area. Preview the clip to confirm before exporting.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Finnish videos?",
        answer:
          "Yes. Finnish is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Finnish.",
      },
      {
        question: "Can I edit the Finnish transcript?",
        answer:
          "Yes. Every caption line in the editor is editable, so a case ending or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Finnish?",
        answer:
          "All 32 caption styles apply to Finnish. Word-by-word styles handle long Finnish words best; preview the style on the clip before export.",
      },
      {
        question: "Does Crayo translate Finnish captions?",
        answer:
          "Yes. The Translate button in the caption list sends Finnish lines to Crayo's translator and returns English or another listed language with the timing unchanged.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, PODCASTS],
  },
  french: {
    code: "fr",
    name: "French",
    nativeName: "Français",
    flag: "FR",
    script: "Latin",
    regions: ["France", "Canada (Quebec)", "Belgium", "Switzerland", "West and North Africa"],
    seoTitle: "French Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe French podcasts, streams and interviews with Crayo and burn in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes French speech and adds animated captions to short clips from podcasts, streams and interviews. It is for French-speaking clippers in France, Quebec, Belgium, Switzerland and francophone Africa who post to TikTok, Reels and Shorts.",
    body: [
      "French short-form video is a big market on its own: France is one of the most active TikTok countries in Europe, French streamers run some of the largest Twitch channels in the world, and the French podcast scene has grown into long interview formats that clippers mine every week. Quebec, Belgium, Switzerland and francophone Africa add audiences who share the same captioned clips.",
      "French uses the Latin alphabet with accents, the cedilla and elisions like l'avenir and qu'il, and French punctuation puts a space before question and exclamation marks. Transcription handles the accents well; the lines to check are proper names, verlan slang in streamer clips and Quebec expressions that differ from Parisian French. Crayo transcribes the speech as French when you pick it.",
      "A practical tip: French sentences run longer than English ones, so a style that shows three or four words at a time keeps captions readable without filling the frame, and a highlight on the active word helps viewers follow fast streamer speech. Preview the style on your clip before exporting.",
    ],
    faqs: [
      {
        question: "Does Crayo caption French videos?",
        answer:
          "Yes. French is in Crayo's language list and covers European, Canadian and African French speech. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the French transcript?",
        answer:
          "Yes. Captions appear as editable lines in the editor, so slang, names and accents can be fixed in place while the timing is kept.",
      },
      {
        question: "Which caption styles work for French?",
        answer:
          "All 32 caption styles apply to French. Preview the style on the clip to confirm accented letters render cleanly in its font.",
      },
      {
        question: "Does Crayo translate French captions?",
        answer:
          "Yes. The Translate button in the caption list converts French lines into English, Spanish or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, VIDEO_EDITOR, CAPTION_STYLES],
  },
  georgian: {
    code: "ka",
    name: "Georgian",
    nativeName: "ქართული",
    flag: "GE",
    script: "Georgian (Mkhedruli)",
    regions: ["Georgia"],
    seoTitle: "Georgian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Georgian podcasts, interviews and streams and adds animated captions in Georgian script to vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Georgian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Georgian creators and media teams who post captioned clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Georgian short-form content is concentrated in Tbilisi, where political interview shows, comedy podcasts and a growing gaming scene produce long recordings. The audience is on TikTok, Instagram and YouTube, and clips with Georgian captions are the main way a long interview reaches younger viewers who scroll with the sound off.",
      "Georgian is written in the Mkhedruli script, which has no uppercase letters and distinctive rounded shapes with long ascenders and descenders. Words are often long and consonant clusters are common, so a caption needs more vertical room than a Latin one. Crayo transcribes the speech into Georgian script when selected; names and Russian or English loanwords are the lines most likely to need an edit.",
      "A practical tip: preview the caption style on your Georgian clip before export, since the fonts differ between styles and Georgian letters need a clean rendering to stay legible on a phone. Keep two or three words on screen at a time to avoid crowding the tall letter shapes.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Georgian videos?",
        answer:
          "Yes. Georgian is in Crayo's language list. Select it before running AutoClip to get a transcript in Georgian script.",
      },
      {
        question: "Can I edit the Georgian transcript?",
        answer:
          "Yes. The editor lists every caption line and allows in-place edits to any word, with the timing left unchanged.",
      },
      {
        question: "Which caption styles work for Georgian script?",
        answer:
          "All 32 caption styles can be applied to Georgian captions. Preview a few on the clip and keep the one whose font renders Mkhedruli letters cleanly.",
      },
      {
        question: "Does Crayo translate Georgian captions?",
        answer:
          "Yes. The Translate button in the caption list turns Georgian lines into English, Russian or another listed language and keeps the original timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  german: {
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    flag: "DE",
    script: "Latin",
    regions: ["Germany", "Austria", "Switzerland"],
    seoTitle: "German Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe German podcasts, streams and interviews with Crayo and export vertical clips with animated captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes German speech and adds animated captions to short clips from podcasts, streams and interviews. It is for clippers and editors in Germany, Austria and Switzerland who post captioned vertical clips to TikTok, Reels and Shorts.",
    body: [
      "German-speaking Europe is the largest podcast market in Europe, with interview, comedy and business podcasts regularly running two hours, and German streamers draw some of the biggest Twitch audiences outside the English-speaking world. Clips from those shows live on TikTok, Instagram Reels and YouTube Shorts, where captions are expected on every cut.",
      "German is written in the Latin alphabet with umlauts and the ß, and it is famous for compound nouns that can run past twenty letters. Transcription handles standard German well; Austrian and Swiss speech are transcribed as German too, though Swiss German dialect is far from the written standard and will need more edits in the caption list. Crayo transcribes the speech as German when selected.",
      "A practical tip: very long compounds cannot be split across lines, so use a caption style that shows one to three words at a time rather than full sentences, and preview clips that contain long nouns to make sure they fit inside the phone safe area at your chosen text size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption German videos?",
        answer:
          "Yes. German is in Crayo's language list and covers speakers from Germany, Austria and Switzerland. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the German transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so an umlaut, a compound or a dialect word can be fixed in place while keeping the timing.",
      },
      {
        question: "Which caption styles work for German?",
        answer:
          "All 32 caption styles apply to German. Word-by-word styles cope best with long compound nouns; preview the style on your clip before export.",
      },
      {
        question: "Does Crayo translate German captions?",
        answer:
          "Yes. The Translate button in the caption list converts German lines into English or another listed language while preserving each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  greek: {
    code: "el",
    name: "Greek",
    nativeName: "Ελληνικά",
    flag: "GR",
    script: "Greek",
    regions: ["Greece", "Cyprus"],
    seoTitle: "Greek Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Greek podcasts and streams and burns animated Greek-script captions into vertical clips for TikTok, Instagram Reels and Shorts.",
    answer:
      "Crayo transcribes Greek speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Greek and Cypriot creators who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Greece and Cyprus have a busy podcast and YouTube interview scene, from comedy and football talk to political shows, and a streamer community that clips its own highlights daily. Greek viewers are heavy Instagram and TikTok users, and a captioned vertical clip is the standard way a long episode gets discovered.",
      "Greek is written in its own alphabet with a single accent mark on the stressed vowel of most words, and that accent is part of correct spelling. Transcription writes Greek script with accents; names, English loanwords and Cypriot dialect words are the usual lines to check. Some younger Greek speakers type in Greeklish, but Crayo transcribes speech into Greek letters, which is what most viewers prefer to read on screen.",
      "A practical tip: Greek letters are a similar width to Latin ones, so most caption styles fit naturally, but preview the style on the clip before export to confirm the accents render clearly in that font. Keep two to four words per caption for fast conversational clips.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Greek videos?",
        answer:
          "Yes. Greek is in Crayo's language list and covers Greek and Cypriot speech. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Greek transcript?",
        answer:
          "Yes. The editor lists every caption and lets you edit any word, including adding a missing accent, with the timing left as it was.",
      },
      {
        question: "Which caption styles work for the Greek alphabet?",
        answer:
          "All 32 caption styles can be applied to Greek captions. Fonts differ by style, so preview a couple on the clip and choose the one where Greek letters look sharpest.",
      },
      {
        question: "Does Crayo translate Greek captions?",
        answer:
          "Yes. The Translate button in the caption list turns Greek lines into English or another listed language and keeps the timing of every line.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, STREAMERS, PODCASTS],
  },
  gujarati: {
    code: "gu",
    name: "Gujarati",
    nativeName: "ગુજરાતી",
    flag: "IN",
    script: "Gujarati",
    regions: ["Gujarat, India", "Gujarati diaspora in the UK, the US and East Africa"],
    seoTitle: "Gujarati Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Gujarati podcasts, interviews and streams with Crayo and export vertical clips with animated Gujarati captions for Shorts, Reels and TikTok.",
    answer:
      "Crayo transcribes Gujarati speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Gujarati creators in India and the diaspora who post captioned clips to YouTube Shorts, Instagram Reels and Facebook.",
    body: [
      "Gujarati content has a strong home base in Ahmedabad, Surat and Vadodara and an unusually large diaspora in the United Kingdom, the United States and East Africa. Business podcasts, comedy shows and devotional and family interview channels produce long episodes, and YouTube Shorts and Instagram Reels are where the clips get their views.",
      "Gujarati is written in its own abugida script, similar to Devanagari but without the horizontal headline, with vowel signs attached around consonants and conjunct forms for consonant clusters. Transcription writes standard Gujarati; speakers mix in Hindi and English constantly, so expect some lines to carry those words. Crayo transcribes the speech as Gujarati when selected.",
      "A practical tip: Gujarati glyphs are intricate, so give captions a large size and a few words at a time, and preview the style on the clip before exporting because the fonts differ between styles and the vowel signs need a clean rendering to read on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Gujarati videos?",
        answer:
          "Yes. Gujarati is in Crayo's language list. Select it before running AutoClip so the audio is transcribed in Gujarati script.",
      },
      {
        question: "Can I edit the Gujarati transcript?",
        answer:
          "Yes. Every caption line is editable in the editor and the timing is unchanged after an edit.",
      },
      {
        question: "Which caption styles work for Gujarati script?",
        answer:
          "All 32 caption styles can be applied. Preview the style on a Gujarati clip and pick one whose font renders the vowel signs and conjuncts cleanly.",
      },
      {
        question: "Does Crayo translate Gujarati captions?",
        answer:
          "Yes. The Translate button in the caption list converts Gujarati lines into English, Hindi or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, CLIPPERS],
  },
  hebrew: {
    code: "he",
    name: "Hebrew",
    nativeName: "עברית",
    flag: "IL",
    script: "Hebrew",
    regions: ["Israel"],
    rtl: true,
    seoTitle: "Hebrew Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Hebrew podcasts, streams and interviews and adds animated Hebrew captions to vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Hebrew speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Israeli podcasters, streamers and clippers who want captioned vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Israel has a dense podcast and YouTube talk scene for a country of its size, with news, comedy and business interview shows running long, and a gaming and streaming community that clips its highlights daily. Israeli audiences are heavy Instagram and TikTok users, and captioned vertical clips are how those long conversations reach them on a commute.",
      "Hebrew is written right to left in a script with no uppercase, and everyday Hebrew is written without vowel points, so a caption relies on context the way printed Hebrew does. Several letters take a different form at the end of a word. Transcription writes unpointed Hebrew, which is what viewers expect; names, English tech words and army slang are the lines most worth a check in the caption list. Crayo transcribes the speech as Hebrew when selected.",
      "A practical tip for Hebrew clips: preview the caption style on the clip before exporting, since right-to-left text and final letter forms look different across the fonts the styles use. Keep captions to a few words so the letters stay large and distinct on a phone screen.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Hebrew videos?",
        answer:
          "Yes. Hebrew is in Crayo's language list. Select it before running AutoClip so the speech is transcribed into Hebrew script.",
      },
      {
        question: "Can I edit the Hebrew transcript?",
        answer:
          "Yes. The editor lists every caption and lets you change any word in place, with the timing kept after edits.",
      },
      {
        question: "Which caption styles work for Hebrew script?",
        answer:
          "All 32 caption styles can be applied to Hebrew captions. Because fonts differ between styles, preview a few on the clip and keep one where the right-to-left text reads cleanly.",
      },
      {
        question: "Does Crayo translate Hebrew captions?",
        answer:
          "Yes. The Translate button in the caption list converts Hebrew lines into English or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, STREAMERS],
  },
  hindi: {
    code: "hi",
    name: "Hindi",
    nativeName: "हिन्दी",
    flag: "IN",
    script: "Devanagari",
    regions: ["India", "Indian diaspora worldwide"],
    seoTitle: "Hindi Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Hindi and Hinglish podcasts, streams and interviews with Crayo and export vertical clips with animated captions for Shorts, Reels and more.",
    answer:
      "Crayo transcribes Hindi speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Hindi and Hinglish creators and the clippers who cut their shows for YouTube Shorts, Instagram Reels and Moj.",
    body: [
      "Hindi is the engine of the Indian creator economy. YouTube Shorts and Instagram Reels dominate, Indian podcasts have grown into multi-hour interview formats with huge audiences, and streamers on YouTube and Loco run long sessions that clippers turn into vertical highlights. Captions are essential because a large share of viewers watch on mobile data with the sound off.",
      "Hindi is written in Devanagari, with vowel signs that attach above, below and beside consonants and a horizontal headline joining the letters of a word. Most Hindi creators actually speak Hinglish, mixing English words freely, and transcription returns those English words in Latin letters inside Devanagari lines, which is how viewers are used to reading them. Crayo transcribes the speech as Hindi when selected.",
      "A practical tip: Devanagari needs vertical room for the signs above and below the headline, so pick a caption style with generous line height and two to four words per caption, and preview it on the clip before export because the fonts differ between styles and the signs need to render cleanly at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Hindi videos?",
        answer:
          "Yes. Hindi is in Crayo's language list and handles Hinglish speech that mixes English words. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Hindi transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so you can correct a Devanagari spelling or swap a word into Latin letters, with the timing kept.",
      },
      {
        question: "Which caption styles work for Devanagari?",
        answer:
          "All 32 caption styles can be applied to Hindi captions. Preview a few on the clip and choose one whose font renders the vowel signs and headline clearly.",
      },
      {
        question: "Does Crayo translate Hindi captions?",
        answer:
          "Yes. The Translate button in the caption list converts Hindi lines into English or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, PODCASTS, CAPTION_STYLES],
  },
  hungarian: {
    code: "hu",
    name: "Hungarian",
    nativeName: "Magyar",
    flag: "HU",
    script: "Latin",
    regions: ["Hungary", "Hungarian communities in Romania, Slovakia and Serbia"],
    seoTitle: "Hungarian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Hungarian podcasts, streams and interviews and burns in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Hungarian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Hungarian podcasters, streamers and clippers posting captioned vertical clips to TikTok, Reels and YouTube Shorts.",
    body: [
      "Hungary has a strong YouTube interview and podcast culture, from political talk and comedy to business shows, and Hungarian streamers have a dedicated Twitch following. Short clips reach viewers on TikTok and Instagram Reels, and Hungarian speakers in Transylvania, Slovakia and Serbia follow the same channels, so captioned clips cross borders.",
      "Hungarian is written in the Latin alphabet with a full set of accented vowels, including the double acute in ő and ű, and it is agglutinative, so words carry suffix after suffix and can get very long. Transcription handles standard speech well; the double acute letters and long suffixed words are the usual lines to check in the caption list. Crayo transcribes the speech as Hungarian when selected.",
      "A practical tip: because Hungarian words are long and the accents are tall, use a caption style that shows one to three words at a time with enough line height, and preview a clip containing a long word to confirm it fits the phone safe area at your text size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Hungarian videos?",
        answer:
          "Yes. Hungarian is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Hungarian.",
      },
      {
        question: "Can I edit the Hungarian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a suffix or a double acute accent can be fixed without affecting the timing.",
      },
      {
        question: "Which caption styles work for Hungarian?",
        answer:
          "All 32 caption styles apply to Hungarian. Word-by-word styles handle long words best; preview on your clip to check how ő and ű render in that font.",
      },
      {
        question: "Does Crayo translate Hungarian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Hungarian lines into English or another listed language with the timing unchanged.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  indonesian: {
    code: "id",
    name: "Indonesian",
    nativeName: "Bahasa Indonesia",
    flag: "ID",
    script: "Latin",
    regions: ["Indonesia"],
    seoTitle: "Indonesian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Indonesian podcasts, streams and interviews with Crayo and export vertical clips with animated captions for TikTok, Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Indonesian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Indonesian creators and clippers serving one of the largest TikTok and YouTube Shorts audiences in the world.",
    body: [
      "Indonesia is one of the biggest short-form markets on the planet, with enormous TikTok and YouTube usage and a podcast scene where talk shows and interview channels run for hours and get clipped relentlessly. Gaming streamers on YouTube and other platforms add more long-form source material, and nearly all of that audience watches on a phone.",
      "Indonesian is written in the Latin alphabet with no diacritics and very regular spelling, which makes it one of the easier languages to caption. The complications are elsewhere: speakers switch between formal Indonesian, Jakarta slang and English within a sentence, and regional languages like Javanese or Sundanese slip in. Crayo transcribes the speech as Indonesian when selected, and slang spellings are easy to adjust in the caption list.",
      "A practical tip: Indonesian clips are often fast, funny and multi-speaker, so a bold word-by-word style with a clear highlight keeps viewers locked in, and three to four words per caption fits most phrases without crowding the frame.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Indonesian videos?",
        answer:
          "Yes. Indonesian is in Crayo's language list. Select it before running AutoClip so the transcript is Indonesian rather than detected as Malay.",
      },
      {
        question: "Can I edit the Indonesian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so slang and names can be adjusted in place with the timing preserved.",
      },
      {
        question: "Which caption styles work for Indonesian?",
        answer:
          "All 32 caption styles apply, since Indonesian uses the plain Latin alphabet. Bold highlight styles suit fast conversational clips.",
      },
      {
        question: "Does Crayo translate Indonesian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Indonesian lines into English or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, STREAMERS, CAPTION_STYLES],
  },
  italian: {
    code: "it",
    name: "Italian",
    nativeName: "Italiano",
    flag: "IT",
    script: "Latin",
    regions: ["Italy", "Switzerland (Ticino)"],
    seoTitle: "Italian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Italian podcasts, streams and interviews and adds animated captions to vertical clips ready for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Italian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Italian podcasters, streamers and clippers who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Italy's podcast scene has matured quickly, with interview, comedy and football shows recording long episodes, and Italian streamers rank among the largest on Twitch in Europe. Italians are heavy Instagram users, so Reels and TikTok are where the clips land, and captions matter because viewers scroll muted on the train as much as anywhere else.",
      "Italian is written in the Latin alphabet with accents mostly on final vowels, and spoken Italian is fast with frequent elisions and apostrophes. Transcription handles the language well; the lines to check are regional dialect words, football names and English tech vocabulary in startup podcasts. Crayo transcribes the speech as Italian when selected.",
      "A practical tip: Italian speakers talk quickly and over each other, so pick a caption style with a strong highlight on the active word and two to four words per caption, which keeps the captions in step with the speech. Preview the style on the clip to confirm the accented final vowels render clearly.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Italian videos?",
        answer:
          "Yes. Italian is in Crayo's language list. Select it before running AutoClip for an Italian transcript.",
      },
      {
        question: "Can I edit the Italian transcript?",
        answer:
          "Yes. Every caption is an editable line in the editor, so a dialect word or a name can be corrected while keeping the timing.",
      },
      {
        question: "Which caption styles work for Italian?",
        answer:
          "All 32 caption styles apply to Italian. Highlight-driven word styles suit fast Italian conversation; preview before exporting.",
      },
      {
        question: "Does Crayo translate Italian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Italian lines into English, Spanish or another listed language and keeps the timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, VIDEO_EDITOR, CAPTION_STYLES],
  },
  japanese: {
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    flag: "JP",
    script: "Kanji, hiragana and katakana",
    regions: ["Japan"],
    seoTitle: "Japanese Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Japanese podcasts, streams and interviews with Crayo and export vertical clips with animated Japanese captions for Shorts, TikTok and Reels.",
    answer:
      "Crayo transcribes Japanese speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Japanese creators, VTuber and streamer clippers, and podcasters who post captioned vertical clips to YouTube Shorts, TikTok and Instagram Reels.",
    body: [
      "Japan has one of the most developed clipping cultures anywhere: streamer and VTuber clip channels are an established genre on YouTube, and Japanese TikTok and Shorts audiences are used to heavy on-screen text. Japanese podcasts and radio-style talk shows add long-form material, and burned-in captions are an expected part of Japanese video rather than an extra.",
      "Japanese is written with a mix of kanji, hiragana and katakana and no spaces between words, so line breaks have to fall at natural phrase boundaries, and a caption of ten characters can carry a full sentence. Transcription chooses kanji based on context, so homophones and names are the lines to check in the caption list. Crayo transcribes the speech as Japanese when selected.",
      "A practical tip: keep Japanese captions short, around six to twelve characters per caption, and large, since characters need more size than Latin letters to read on a phone. Preview the style on the clip before export because the fonts differ between the 32 styles and characters need a clean, heavy weight to stay legible.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Japanese videos?",
        answer:
          "Yes. Japanese is in Crayo's language list. Select it before running AutoClip so the speech is transcribed into Japanese script.",
      },
      {
        question: "Can I edit the Japanese transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a wrong kanji or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Japanese characters?",
        answer:
          "All 32 caption styles can be applied to Japanese captions. Preview a few on the clip and choose one whose font renders kanji and kana crisply at phone size.",
      },
      {
        question: "Does Crayo translate Japanese captions?",
        answer:
          "Yes. The Translate button in the caption list converts Japanese lines into English or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, CLIPPERS],
  },
  kannada: {
    code: "kn",
    name: "Kannada",
    nativeName: "ಕನ್ನಡ",
    flag: "IN",
    script: "Kannada",
    regions: ["Karnataka, India"],
    seoTitle: "Kannada Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Kannada podcasts, interviews and streams and adds animated Kannada captions to vertical clips for Shorts, Reels and more.",
    answer:
      "Crayo transcribes Kannada speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Kannada creators in Karnataka who post captioned vertical clips to YouTube Shorts and Instagram Reels.",
    body: [
      "Kannada content centres on Bengaluru and the rest of Karnataka, where film interview channels, comedy podcasts, tech talk and devotional shows produce long recordings. YouTube Shorts leads for Kannada audiences, with Instagram Reels close behind, and clips with Kannada captions perform noticeably better with viewers who scroll with the sound off.",
      "Kannada is written in its own rounded abugida script, where vowel signs attach to consonants and consonant clusters form stacked conjuncts. Speakers mix in English freely, especially in Bengaluru tech and startup conversations, and those words usually come through in Latin letters. Crayo transcribes the speech as Kannada when selected; names and English terms are the lines to check in the caption list.",
      "A practical tip: Kannada glyphs are wide and round, so use a larger text size with two or three words per caption, and preview the style on the clip before export because the fonts differ across the 32 styles and conjunct forms need a clean rendering to stay readable.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Kannada videos?",
        answer:
          "Yes. Kannada is in Crayo's language list. Select it before running AutoClip to get a transcript in Kannada script.",
      },
      {
        question: "Can I edit the Kannada transcript?",
        answer:
          "Yes. The editor lists every caption line and lets you edit any word in place, with the timing unchanged.",
      },
      {
        question: "Which caption styles work for Kannada script?",
        answer:
          "All 32 caption styles can be applied. Preview a few on a Kannada clip and keep the one whose font renders the rounded glyphs and conjuncts cleanly.",
      },
      {
        question: "Does Crayo translate Kannada captions?",
        answer:
          "Yes. The Translate button in the caption list converts Kannada lines into English, Hindi or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, CLIPPERS],
  },
  kazakh: {
    code: "kk",
    name: "Kazakh",
    nativeName: "Қазақша",
    flag: "KZ",
    script: "Cyrillic",
    regions: ["Kazakhstan"],
    seoTitle: "Kazakh Auto Captions and Subtitles for Short Clips",
    seoDescription:
      "Transcribe Kazakh podcasts, interviews and streams with Crayo and export vertical clips with animated captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Kazakh speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Kazakh-language creators and media teams who post captioned vertical clips to TikTok, Instagram and YouTube Shorts.",
    body: [
      "Kazakh-language content has grown fast as Kazakhstan's young population shifts to Kazakh-first media, with interview podcasts, comedy shows and streamers in Almaty and Astana producing long recordings. TikTok and Instagram are the main short-form platforms, and YouTube Shorts carries the longer interview clips.",
      "Kazakh is currently written in Cyrillic with nine extra letters that Russian does not use, such as ә, ғ, қ, ң, ө, ұ, ү and һ, while a transition to a Latin alphabet is still under way. Speech also mixes Kazakh and Russian within a conversation. Crayo transcribes Kazakh into Cyrillic when selected, and choosing it explicitly rather than auto detect avoids the transcript coming back as Russian.",
      "A practical tip: the extra Kazakh letters have small distinguishing marks, so use a caption style with large, heavy text and preview it on the clip before exporting to confirm the font renders those letters clearly. Two to three words per caption keeps Kazakh readable on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Kazakh videos?",
        answer:
          "Yes. Kazakh is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Kazakh rather than detected as Russian.",
      },
      {
        question: "Can I edit the Kazakh transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a Russian word or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Kazakh Cyrillic?",
        answer:
          "All 32 caption styles can be applied. Preview the style on the clip and choose one whose font renders the Kazakh-specific letters cleanly.",
      },
      {
        question: "Does Crayo translate Kazakh captions?",
        answer:
          "Yes. The Translate button in the caption list converts Kazakh lines into Russian, English or another listed language with the timing preserved.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  korean: {
    code: "ko",
    name: "Korean",
    nativeName: "한국어",
    flag: "KR",
    script: "Hangul",
    regions: ["South Korea", "Korean diaspora"],
    seoTitle: "Korean Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Korean podcasts, streams and interviews and adds animated Hangul captions to vertical clips for Shorts, TikTok and Reels.",
    answer:
      "Crayo transcribes Korean speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Korean streamer and podcast clippers, and creators posting captioned vertical clips to YouTube Shorts, TikTok and Instagram Reels.",
    body: [
      "Korean video culture invented much of the on-screen text style that short-form borrowed, and Korean streamers on platforms like Chzzk and SOOP produce hours of content that clip channels cut daily. YouTube Shorts and Instagram Reels dominate for Korean audiences, and viewers expect captions on every clip because that is how Korean variety and streaming video has always looked.",
      "Korean is written in Hangul, where letters group into syllable blocks, with spaces between words and occasional Hanja or English mixed in. Transcription handles standard speech well; the lines to check are spacing between words, which Korean speakers themselves disagree on, and names, game terms and internet slang. Crayo transcribes the speech as Korean when selected.",
      "A practical tip: Hangul syllable blocks are compact and read well in bold caption styles, so three to five words per caption works, but preview the style on the clip before export because the fonts differ between the 32 styles and the blocks need a clean, heavy weight at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Korean videos?",
        answer:
          "Yes. Korean is in Crayo's language list. Select it before running AutoClip so the speech is transcribed into Hangul.",
      },
      {
        question: "Can I edit the Korean transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so spacing, slang and names can be adjusted without changing the timing.",
      },
      {
        question: "Which caption styles work for Hangul?",
        answer:
          "All 32 caption styles can be applied to Korean captions. Preview a few on the clip and keep the one whose font renders Hangul blocks crisply.",
      },
      {
        question: "Does Crayo translate Korean captions?",
        answer:
          "Yes. The Translate button in the caption list converts Korean lines into English or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, CLIPPERS],
  },
  latvian: {
    code: "lv",
    name: "Latvian",
    nativeName: "Latviešu",
    flag: "LV",
    script: "Latin",
    regions: ["Latvia"],
    seoTitle: "Latvian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Transcribe Latvian podcasts, interviews and streams with Crayo and burn in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Latvian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Latvian creators and media teams who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Latvian short-form content is centred on Riga, where interview podcasts, comedy channels and public-media talk shows produce long episodes, and a small but active streamer scene clips its own highlights. The audience is on TikTok, Instagram and YouTube, and captions help clips reach the many Latvians who also follow Russian-language media.",
      "Latvian is written in the Latin alphabet with macrons on long vowels, a cedilla-like mark on ģ, ķ, ļ and ņ, and háčeks on č, š and ž. Those marks are part of correct spelling, so a transcript that drops a macron reads as a mistake to native viewers. Crayo transcribes the speech as Latvian when selected; a quick pass through the caption list catches the few diacritics that need fixing.",
      "A practical tip: the macrons and other marks sit above and below the letters, so choose a caption style with enough line height and a large text size, and preview it on the clip before export to confirm the font renders the Latvian letters cleanly.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Latvian videos?",
        answer:
          "Yes. Latvian is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Latvian.",
      },
      {
        question: "Can I edit the Latvian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a macron or a name can be corrected while the timing stays the same.",
      },
      {
        question: "Which caption styles work for Latvian?",
        answer:
          "All 32 caption styles apply, since Latvian uses the Latin alphabet. Preview the style on your clip to check how the macron and cedilla letters render in its font.",
      },
      {
        question: "Does Crayo translate Latvian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Latvian lines into English, Russian or another listed language with the timing unchanged.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  lithuanian: {
    code: "lt",
    name: "Lithuanian",
    nativeName: "Lietuvių",
    flag: "LT",
    script: "Latin",
    regions: ["Lithuania"],
    seoTitle: "Lithuanian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Lithuanian podcasts, interviews and streams and adds animated captions to vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Lithuanian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Lithuanian podcasters, streamers and clippers who post captioned vertical clips to TikTok, Reels and Shorts.",
    body: [
      "Lithuania has an energetic podcast and YouTube scene in Vilnius and Kaunas, with business, comedy and basketball talk shows running long, and a streamer community that grew alongside the country's gaming industry. Clips reach viewers on TikTok and Instagram Reels, and captions are the difference between a muted scroll-past and a watched clip.",
      "Lithuanian is written in the Latin alphabet with ogoneks on ą, ę, į and ų, a macron on ū, a dot on ė and háčeks on č, š and ž. It is highly inflected, so word endings change constantly and names take Lithuanian suffixes. Crayo transcribes the speech as Lithuanian when selected; endings on foreign names are the lines most worth checking in the caption list.",
      "A practical tip: ogoneks hang below the baseline, so choose a caption style with a large text size and some vertical breathing room, and preview it on the clip before export to confirm the font renders the Lithuanian letters properly.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Lithuanian videos?",
        answer:
          "Yes. Lithuanian is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Lithuanian.",
      },
      {
        question: "Can I edit the Lithuanian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so an ending or a diacritic can be fixed in place with the timing kept.",
      },
      {
        question: "Which caption styles work for Lithuanian?",
        answer:
          "All 32 caption styles apply, as Lithuanian uses the Latin alphabet. Preview the style on your clip to check how the ogonek and dotted letters render.",
      },
      {
        question: "Does Crayo translate Lithuanian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Lithuanian lines into English or another listed language and keeps each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  macedonian: {
    code: "mk",
    name: "Macedonian",
    nativeName: "Македонски",
    flag: "MK",
    script: "Cyrillic",
    regions: ["North Macedonia"],
    seoTitle: "Macedonian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Transcribe Macedonian podcasts, interviews and streams with Crayo and export vertical clips with animated Cyrillic captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Macedonian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for creators in North Macedonia and the diaspora who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Macedonian short-form content comes mostly from Skopje, where podcast interviews, comedy shows and music channels produce long recordings, with a diaspora in Australia, Switzerland and Germany following along. Clips travel on TikTok and Instagram Reels, and captions help them reach neighbouring Bulgarian and Serbian speakers who read Cyrillic easily.",
      "Macedonian is written in Cyrillic with letters that neither Russian nor Serbian uses in the same way, such as ѓ, ќ, ѕ, љ and њ. Speech often borrows from Serbian and English, and transcription may spell those borrowings differently from the speaker. Crayo transcribes the speech as Macedonian when selected; picking it explicitly rather than auto detect avoids a Serbian or Bulgarian transcript.",
      "A practical tip: the Macedonian-specific letters are distinguished by small strokes, so use a caption style with large, heavy text and preview it on the clip before export to confirm the font renders them clearly. Two to three words per caption keeps Cyrillic readable on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Macedonian videos?",
        answer:
          "Yes. Macedonian is in Crayo's language list. Select it before running AutoClip so the transcript uses Macedonian Cyrillic.",
      },
      {
        question: "Can I edit the Macedonian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a borrowed word or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Macedonian Cyrillic?",
        answer:
          "All 32 caption styles can be applied. Preview a couple on the clip and keep the one whose font renders the Macedonian letters cleanly.",
      },
      {
        question: "Does Crayo translate Macedonian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Macedonian lines into English or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  malay: {
    code: "ms",
    name: "Malay",
    nativeName: "Bahasa Melayu",
    flag: "MY",
    script: "Latin",
    regions: ["Malaysia", "Singapore", "Brunei"],
    seoTitle: "Malay Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Malay podcasts, streams and interviews and burns in animated captions on vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Malay speech and adds animated captions to short clips from podcasts, streams and interviews. It is for creators in Malaysia, Singapore and Brunei who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Malaysia is one of the most TikTok-heavy countries in Southeast Asia, and Malay podcasts, radio-style interview shows and gaming streamers produce long recordings that clippers cut for TikTok, Reels and Shorts. Singapore and Brunei share the audience, and clips with Malay captions circulate freely across all three.",
      "Malay is written in the Latin alphabet with no diacritics, but everyday speech mixes Malay with English and sometimes Chinese or Tamil words within a single sentence, a style often called bahasa rojak. Crayo transcribes the speech as Malay when selected, and English words generally come through as English. Choosing Malay explicitly rather than auto detect avoids the transcript being returned as Indonesian, which is closely related.",
      "A practical tip: Malay conversations are lively and multi-speaker, so a bold caption style with a strong highlight on the active word keeps viewers following, and three to four words per caption fits most phrases without crowding the frame.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Malay videos?",
        answer:
          "Yes. Malay is in Crayo's language list as its own entry. Select it before running AutoClip so the transcript is Malay rather than Indonesian.",
      },
      {
        question: "Can I edit the Malay transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so mixed-language words and names can be adjusted with the timing preserved.",
      },
      {
        question: "Which caption styles work for Malay?",
        answer:
          "All 32 caption styles apply, since Malay uses the plain Latin alphabet. Highlight styles suit fast conversational clips.",
      },
      {
        question: "Does Crayo translate Malay captions?",
        answer:
          "Yes. The Translate button in the caption list converts Malay lines into English, Indonesian or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, CAPTION_STYLES, PODCASTS],
  },
  marathi: {
    code: "mr",
    name: "Marathi",
    nativeName: "मराठी",
    flag: "IN",
    script: "Devanagari",
    regions: ["Maharashtra, India"],
    seoTitle: "Marathi Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Marathi podcasts, interviews and streams with Crayo and export vertical clips with animated Devanagari captions for YouTube Shorts and Reels.",
    answer:
      "Crayo transcribes Marathi speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Marathi creators in Maharashtra who post captioned vertical clips to YouTube Shorts, Instagram Reels and Facebook.",
    body: [
      "Marathi content is rooted in Mumbai and Pune, where film and theatre interview channels, comedy podcasts, business shows and political talk produce long recordings. YouTube Shorts and Instagram Reels are the main short-form platforms for Marathi audiences, and captions give a clip a second chance with viewers who scroll muted.",
      "Marathi is written in Devanagari, the same script as Hindi, with vowel signs above, below and beside the consonants and a headline joining the letters of a word, plus a few Marathi-specific letters and conjunct forms. Speakers mix in Hindi and English, and transcription may return those words in Devanagari or Latin letters. Crayo transcribes the speech as Marathi when selected; choosing it explicitly avoids a Hindi transcript.",
      "A practical tip: Devanagari needs vertical room, so pick a caption style with generous line height and two to four words per caption, and preview it on the clip before export because the fonts differ across the 32 styles and the vowel signs need a clean rendering at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Marathi videos?",
        answer:
          "Yes. Marathi is in Crayo's language list as its own entry. Select it before running AutoClip so the speech is transcribed as Marathi rather than Hindi.",
      },
      {
        question: "Can I edit the Marathi transcript?",
        answer:
          "Yes. Every caption line is editable in the editor and the timing is kept after any edit.",
      },
      {
        question: "Which caption styles work for Devanagari?",
        answer:
          "All 32 caption styles can be applied to Marathi captions. Preview a few on the clip and keep one whose font renders the vowel signs and headline cleanly.",
      },
      {
        question: "Does Crayo translate Marathi captions?",
        answer:
          "Yes. The Translate button in the caption list converts Marathi lines into English, Hindi or another listed language with the timing unchanged.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, CLIPPERS],
  },
  nepali: {
    code: "ne",
    name: "Nepali",
    nativeName: "नेपाली",
    flag: "NP",
    script: "Devanagari",
    regions: ["Nepal", "Nepali communities in India and the Gulf"],
    seoTitle: "Nepali Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Nepali podcasts, interviews and streams and adds animated Devanagari captions to vertical clips for TikTok, YouTube Shorts and Reels.",
    answer:
      "Crayo transcribes Nepali speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Nepali creators at home and abroad who post captioned vertical clips to TikTok, YouTube Shorts and Instagram Reels.",
    body: [
      "Nepal has a surprisingly large podcast and YouTube interview scene, with Kathmandu talk shows, comedy podcasts and music interviews running long, and a huge audience of Nepalis working abroad in India, the Gulf and Malaysia who keep up through short clips. TikTok and YouTube Shorts are the main platforms, and captions matter for viewers watching on limited mobile data.",
      "Nepali is written in Devanagari, with vowel signs around the consonants and a headline across each word, and speech mixes in English and Hindi words, particularly in tech and business conversations. Transcription writes standard Nepali; borrowed words may come back in Devanagari or Latin letters. Crayo transcribes the speech as Nepali when selected, and choosing it explicitly avoids a Hindi transcript.",
      "A practical tip: give Devanagari captions a large text size and enough line height for the signs above and below the headline, use two to four words per caption, and preview the style on the clip before export because the fonts differ between styles.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Nepali videos?",
        answer:
          "Yes. Nepali is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Nepali rather than detected as Hindi.",
      },
      {
        question: "Can I edit the Nepali transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a borrowed word or a name can be fixed in place with the timing preserved.",
      },
      {
        question: "Which caption styles work for Devanagari?",
        answer:
          "All 32 caption styles can be applied to Nepali captions. Preview a few on the clip and choose one whose font renders Devanagari cleanly at phone size.",
      },
      {
        question: "Does Crayo translate Nepali captions?",
        answer:
          "Yes. The Translate button in the caption list converts Nepali lines into English, Hindi or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  norwegian: {
    code: "no",
    name: "Norwegian",
    nativeName: "Norsk",
    flag: "NO",
    script: "Latin",
    regions: ["Norway"],
    seoTitle: "Norwegian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Norwegian podcasts, streams and interviews with Crayo and export vertical clips with animated captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Norwegian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Norwegian podcasters, streamers and clippers who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Norway is one of the most podcast-dense countries in the world per capita, with comedy, interview and true crime shows recording long episodes, and Norwegian streamers have a committed Twitch following. Short clips land on TikTok and Instagram Reels, and Swedish and Danish viewers follow along because the written languages are close enough to read.",
      "Norwegian is written in the Latin alphabet with æ, ø and å, and it has two written standards, Bokmål and Nynorsk, plus strong regional dialects that differ a lot from either. Transcription returns Bokmål-style spelling for most speech, so Nynorsk users and dialect speakers may want to adjust lines in the caption list. Crayo transcribes the speech as Norwegian when selected.",
      "A practical tip: dialect-heavy speech from Bergen or the north can produce more edits than Oslo speech, so skim the first minute of captions before cutting the whole episode. A caption style showing two to four words at a time keeps Norwegian readable and keeps æ, ø and å distinct on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Norwegian videos?",
        answer:
          "Yes. Norwegian is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Norwegian rather than Danish or Swedish.",
      },
      {
        question: "Can I edit the Norwegian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a dialect word or a Nynorsk spelling can be set in place while the timing is kept.",
      },
      {
        question: "Which caption styles work for Norwegian?",
        answer:
          "All 32 caption styles apply to Norwegian. Preview the style on your clip to confirm æ, ø and å render cleanly in its font.",
      },
      {
        question: "Does Crayo translate Norwegian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Norwegian lines into English or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  persian: {
    code: "fa",
    name: "Persian",
    nativeName: "فارسی",
    flag: "IR",
    script: "Perso-Arabic",
    regions: ["Iran", "Afghanistan (Dari)", "Tajikistan", "Persian diaspora"],
    rtl: true,
    seoTitle: "Persian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Persian and Farsi podcasts, streams and interviews and adds animated captions to vertical clips for Reels, Shorts and TikTok.",
    answer:
      "Crayo transcribes Persian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Farsi-speaking creators in Iran and the diaspora who post captioned vertical clips to Instagram Reels, YouTube Shorts and TikTok.",
    body: [
      "Persian-language short-form content has an enormous audience inside Iran, where Instagram is the dominant social platform, and a large diaspora in Canada, Germany, the United States and the Gulf that follows Persian podcasts and YouTube interview shows. Dari speakers in Afghanistan and the diaspora share much of the same content, and captions carry clips across all of it.",
      "Persian is written right to left in the Perso-Arabic script, with four letters Arabic does not have and a cursive form where letters join and change shape by position. Short vowels are not written, and the zero-width joiner matters for correct word shapes in some compounds. Crayo transcribes the speech into Persian script when selected; names, English loanwords and colloquial Tehrani forms are the lines to check in the caption list.",
      "A practical tip for Persian clips: preview the caption style on the actual clip before export, since right-to-left cursive text looks different across the fonts the 32 styles use, and keep captions to a few words so the joined letter forms stay large and legible on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Persian videos?",
        answer:
          "Yes. Persian is in Crayo's language list and covers Farsi speech. Select it before running AutoClip so the audio is transcribed into Persian script.",
      },
      {
        question: "Can I edit the Persian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a colloquial form or a name can be corrected with the timing left unchanged.",
      },
      {
        question: "Which caption styles work for Persian script?",
        answer:
          "All 32 caption styles can be applied to Persian captions. Fonts differ between styles, so preview a few on the clip and keep the one where the cursive script reads cleanly.",
      },
      {
        question: "Does Crayo translate Persian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Persian lines into English or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CLIPPERS],
  },
  polish: {
    code: "pl",
    name: "Polish",
    nativeName: "Polski",
    flag: "PL",
    script: "Latin",
    regions: ["Poland", "Polish diaspora in the UK, Germany and the US"],
    seoTitle: "Polish Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Polish podcasts, streams and interviews with Crayo and burn in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Polish speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Polish podcasters, streamers and clippers who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Poland has one of the biggest YouTube and streaming scenes in Europe, with interview podcasts and comedy shows recording multi-hour episodes and Polish streamers filling Twitch top charts. Short clips reach a huge TikTok audience at home and a diaspora of millions in the United Kingdom, Germany and the United States who watch Polish content daily.",
      "Polish is written in the Latin alphabet with ą, ę, ć, ł, ń, ó, ś, ź and ż, and it stacks consonants into clusters that look intimidating but transcribe reliably. The lines most likely to need an edit are names with case endings, gaming slang and the difference between ż and rz, which sound identical. Crayo transcribes the speech as Polish when selected.",
      "A practical tip: Polish words are long and the diacritics are small, so use a caption style with large, heavy text and two to three words per caption, and preview a clip containing a long word to confirm it fits the phone safe area at your chosen size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Polish videos?",
        answer:
          "Yes. Polish is in Crayo's language list. Select it before running AutoClip for a Polish transcript with the correct diacritics.",
      },
      {
        question: "Can I edit the Polish transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a case ending or an ż and rz mix-up can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Polish?",
        answer:
          "All 32 caption styles apply to Polish. Preview the style on your clip to confirm the ogonek and stroke letters render cleanly in its font.",
      },
      {
        question: "Does Crayo translate Polish captions?",
        answer:
          "Yes. The Translate button in the caption list converts Polish lines into English, German or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, VIDEO_EDITOR, CAPTION_STYLES],
  },
  portuguese: {
    code: "pt",
    name: "Portuguese",
    nativeName: "Português",
    flag: "PT",
    script: "Latin",
    regions: ["Brazil", "Portugal", "Angola", "Mozambique"],
    seoTitle: "Portuguese Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Brazilian and European Portuguese podcasts, streams and interviews and adds animated captions to clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Portuguese speech and adds animated captions to short clips from podcasts, streams and interviews. It is for clippers and creators in Brazil, Portugal and lusophone Africa who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Brazil is one of the largest short-form audiences on earth and one of the most active clipping markets, with podcast networks, streamers and interview channels producing hours of Portuguese talk every day that clippers turn into vertical cuts for TikTok, Reels and Shorts. Portugal and Angola add further audiences who watch the same clips.",
      "Portuguese is written in the Latin alphabet with accents, the tilde in ão and the cedilla in ç, and Brazilian and European Portuguese differ in vocabulary, pronunciation and some spellings. Crayo transcribes both as Portuguese when selected; names, slang and the gerund forms Brazilians favour are the lines to check in the caption list, and the tilde and cedilla are worth a glance.",
      "A practical tip: Brazilian speakers are fast and expressive, which plays well in clips, so pick a caption style with a strong highlight on the active word and keep two to four words per caption. Preview the style on the clip to confirm ã, õ and ç render cleanly in its font.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Portuguese videos?",
        answer:
          "Yes. Portuguese is in Crayo's language list and covers both Brazilian and European speech. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Portuguese transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so slang, names and accents can be corrected with the timing kept.",
      },
      {
        question: "Which caption styles work for Portuguese?",
        answer:
          "All 32 caption styles apply to Portuguese. Highlight-driven word styles suit fast Brazilian conversation; preview before exporting.",
      },
      {
        question: "Does Crayo translate Portuguese captions?",
        answer:
          "Yes. The Translate button in the caption list converts Portuguese lines into English, Spanish or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, PODCASTS, CAPTION_STYLES],
  },
  punjabi: {
    code: "pa",
    name: "Punjabi",
    nativeName: "ਪੰਜਾਬੀ",
    flag: "IN",
    script: "Gurmukhi",
    regions: ["Punjab, India", "Punjabi diaspora in Canada, the UK and the US"],
    seoTitle: "Punjabi Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Punjabi podcasts, interviews and streams with Crayo and export vertical clips with animated Gurmukhi captions for Shorts, Reels and TikTok.",
    answer:
      "Crayo transcribes Punjabi speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Punjabi creators in India and the diaspora in Canada, the United Kingdom and the United States who post captioned clips to YouTube Shorts, Instagram Reels and TikTok.",
    body: [
      "Punjabi content has a home audience in Indian Punjab and one of the most influential diasporas in short-form video, centred on Canada and the United Kingdom, where Punjabi music, comedy and interview channels have huge reach. Podcast interviews with artists and long comedy shows are prime clipping material, and YouTube Shorts and Instagram Reels carry the cuts.",
      "Punjabi in India is written in Gurmukhi, an abugida with vowel signs around the consonants and a headline joining letters, while Punjabi in Pakistan uses the Shahmukhi script. Crayo transcribes the speech into Gurmukhi when Punjabi is selected. Speakers mix in English and Hindi freely, so borrowed words may come back in either script and are easy to adjust in the caption list.",
      "A practical tip: Gurmukhi needs vertical space for the signs above and below the headline, so use a caption style with generous line height, two to four words per caption, and preview it on the clip before export because the fonts differ between the 32 styles.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Punjabi videos?",
        answer:
          "Yes. Punjabi is in Crayo's language list and is transcribed into Gurmukhi script. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Punjabi transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a borrowed word or a name can be corrected with the timing preserved.",
      },
      {
        question: "Which caption styles work for Gurmukhi?",
        answer:
          "All 32 caption styles can be applied to Punjabi captions. Preview a few on the clip and keep the one whose font renders Gurmukhi cleanly at phone size.",
      },
      {
        question: "Does Crayo translate Punjabi captions?",
        answer:
          "Yes. The Translate button in the caption list converts Punjabi lines into English, Hindi or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, CAPTION_STYLES, PODCASTS],
  },
  romanian: {
    code: "ro",
    name: "Romanian",
    nativeName: "Română",
    flag: "RO",
    script: "Latin",
    regions: ["Romania", "Moldova", "Romanian diaspora in Italy, Spain and Germany"],
    seoTitle: "Romanian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Romanian podcasts, streams and interviews and burns in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Romanian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Romanian and Moldovan podcasters, streamers and clippers posting captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Romania has a fast-growing podcast scene, with interview, comedy and business shows recording long episodes in Bucharest and Cluj, and a strong streamer community. The audience is on TikTok and Instagram Reels at home and in a diaspora of millions in Italy, Spain, Germany and the United Kingdom, plus Moldova, all of whom watch the same captioned clips.",
      "Romanian is written in the Latin alphabet with ă, â, î, ș and ț, and the comma-below letters ș and ț are often replaced with cedilla versions in older text, which looks wrong to many readers. Transcription handles standard Romanian well; names, English tech words and Moldovan regional vocabulary are the lines to check in the caption list. Crayo transcribes the speech as Romanian when selected.",
      "A practical tip: Romanian sentences are long and the diacritics are small, so use a caption style with large text, two to four words per caption and a clear highlight on the active word, and preview it on the clip to confirm ș and ț render correctly in that font.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Romanian videos?",
        answer:
          "Yes. Romanian is in Crayo's language list and covers speech from Romania and Moldova. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Romanian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a diacritic or a name can be fixed in place with the timing unchanged.",
      },
      {
        question: "Which caption styles work for Romanian?",
        answer:
          "All 32 caption styles apply to Romanian. Preview the style on your clip to check how ă, â, î, ș and ț render in its font.",
      },
      {
        question: "Does Crayo translate Romanian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Romanian lines into English, Italian or another listed language and keeps each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  russian: {
    code: "ru",
    name: "Russian",
    nativeName: "Русский",
    flag: "RU",
    script: "Cyrillic",
    regions: ["Russia", "Kazakhstan", "Russian-speaking diaspora in Europe, Israel and the US"],
    seoTitle: "Russian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Russian podcasts, streams and interviews with Crayo and export vertical clips with animated Cyrillic captions for Shorts, TikTok and Reels.",
    answer:
      "Crayo transcribes Russian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Russian-speaking podcasters, streamers and clippers across Russia, Central Asia, the Baltics, Israel and the wider diaspora who post to YouTube Shorts, TikTok and Instagram Reels.",
    body: [
      "Russian-language YouTube is one of the largest in the world, with interview channels, podcasts and streamers recording hours of talk that clip channels cut daily. YouTube Shorts and Telegram are where many of those clips travel, with TikTok and Instagram Reels strong among the diaspora in Germany, Israel, the Baltics and the United States, and Russian captions make a clip readable across all of those markets.",
      "Russian is written in Cyrillic, with the letter ё usually written as е in everyday text and the soft and hard signs changing pronunciation without a sound of their own. Transcription handles standard Russian well; the lines to check are names, internet slang and English loanwords that speakers pronounce with Russian endings. Crayo transcribes the speech as Russian when selected.",
      "A practical tip: Cyrillic words are slightly wider than Latin ones, so two to three words per caption keeps the text large, and a caption style with a strong highlight on the active word helps viewers follow fast interview crosstalk. Preview the style on the clip to see how its font handles Cyrillic before exporting.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Russian videos?",
        answer:
          "Yes. Russian is in Crayo's language list. Select it before running AutoClip for a standard Russian transcript in Cyrillic.",
      },
      {
        question: "Can I edit the Russian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a name or a loanword can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Cyrillic?",
        answer:
          "All 32 caption styles can be applied to Russian captions. Preview a couple on the clip and keep the one where Cyrillic reads cleanly in that font.",
      },
      {
        question: "Does Crayo translate Russian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Russian lines into English or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, PODCASTS, CAPTION_STYLES],
  },
  serbian: {
    code: "sr",
    name: "Serbian",
    nativeName: "Српски",
    flag: "RS",
    script: "Cyrillic and Latin",
    regions: ["Serbia", "Montenegro", "Serbian diaspora in Austria, Germany and Switzerland"],
    seoTitle: "Serbian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Serbian podcasts, streams and interviews and adds animated captions to vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Serbian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Serbian podcasters, streamers and clippers in Serbia, Montenegro and the diaspora who post captioned vertical clips to TikTok, Reels and Shorts.",
    body: [
      "Serbia has one of the liveliest podcast and YouTube interview scenes in the Balkans, with long-form talk shows and comedy podcasts out of Belgrade and Novi Sad, and a streamer community whose clips circulate across the whole region. The audience is on TikTok and Instagram Reels, and a captioned clip reaches Croatian, Bosnian and Montenegrin viewers as easily as Serbian ones.",
      "Serbian is unusual in using two scripts, Cyrillic and Latin, interchangeably, with a one-to-one mapping between them. The transcript Crayo returns may come in either script depending on the model output, so check the first few captions and, if you want the other script, edit the lines in the caption list. Names and diacritics such as č, ć, đ, š and ž in the Latin form are the other lines worth a glance.",
      "A practical tip: decide on one script for the whole clip and keep it consistent, since mixing the two in one caption track looks careless to native viewers. A caption style showing two to three words at a time keeps either script large and readable on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Serbian videos?",
        answer:
          "Yes. Serbian is in Crayo's language list as its own entry. Select it before running AutoClip so the transcript uses Serbian forms rather than Croatian or Bosnian.",
      },
      {
        question: "Can I edit the Serbian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so you can switch a line between Cyrillic and Latin or fix a name without changing the timing.",
      },
      {
        question: "Which caption styles work for Serbian?",
        answer:
          "All 32 caption styles can be applied to Serbian in either script. Preview the style on the clip to confirm its font renders Cyrillic or the accented Latin letters cleanly.",
      },
      {
        question: "Does Crayo translate Serbian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Serbian lines into English, German or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  slovak: {
    code: "sk",
    name: "Slovak",
    nativeName: "Slovenčina",
    flag: "SK",
    script: "Latin",
    regions: ["Slovakia"],
    seoTitle: "Slovak Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Slovak podcasts, streams and interviews with Crayo and burn in animated captions on vertical clips for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Slovak speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Slovak podcasters, streamers and clippers who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Slovakia shares much of its media space with the Czech Republic, so Slovak podcasts, interview shows and streamers reach both countries, and clips with Slovak captions are read without trouble by Czech viewers. The home audience is on TikTok and Instagram Reels, and Bratislava-based podcast and comedy channels produce long episodes well suited to clipping.",
      "Slovak is written in the Latin alphabet with a wide set of diacritics, including ä, ľ, ĺ, ŕ, ô and the háček letters č, š, ž and ď, ť, ň. Because Czech and Slovak are so close, auto detection can return a Czech transcript, so choose Slovak explicitly in the language picker. Names and the soft consonants are the lines to check in the caption list after transcription.",
      "A practical tip: Slovak diacritics are small and sit close to the letters, so a caption style with large, heavy text and two to three words per caption keeps them legible, and previewing the style on the clip before export shows how its font handles letters like ľ and ô.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Slovak videos?",
        answer:
          "Yes. Slovak is in Crayo's language list as its own entry. Select it before running AutoClip so the speech is transcribed as Slovak rather than Czech.",
      },
      {
        question: "Can I edit the Slovak transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a soft consonant or a name can be corrected with the timing left unchanged.",
      },
      {
        question: "Which caption styles work for Slovak?",
        answer:
          "All 32 caption styles apply to Slovak. Preview the style on your clip to check how its font renders the Slovak diacritics.",
      },
      {
        question: "Does Crayo translate Slovak captions?",
        answer:
          "Yes. The Translate button in the caption list converts Slovak lines into English, Czech or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, STREAMERS],
  },
  slovenian: {
    code: "sl",
    name: "Slovenian",
    nativeName: "Slovenščina",
    flag: "SI",
    script: "Latin",
    regions: ["Slovenia"],
    seoTitle: "Slovenian Auto Captions and Subtitles for Clips",
    seoDescription:
      "Crayo transcribes Slovenian podcasts, interviews and streams and adds animated captions to vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Slovenian speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Slovenian creators and media teams who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Slovenia is a small market with an outsized appetite for podcasts, from business and sports interview shows to comedy, recorded mostly in Ljubljana and Maribor. Clips reach viewers on TikTok and Instagram Reels, and because the home audience is only two million, captions help clips travel to Slovenians abroad and to neighbouring viewers who understand some Slovenian.",
      "Slovenian is written in the Latin alphabet with č, š and ž, and it keeps the dual grammatical number, so verb and noun endings change in ways transcription usually handles but names sometimes trip. Regional dialects differ noticeably from the standard, and speech borrows from German, Italian and English depending on the region. Crayo transcribes the speech as Slovenian when selected.",
      "A practical tip: for dialect-heavy regional podcasts, skim the first minute of captions before cutting the full episode to catch recurring spellings you want to standardise, then pick a caption style that shows two to four words at a time so the háček letters stay large on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Slovenian videos?",
        answer:
          "Yes. Slovenian is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Slovenian rather than Croatian.",
      },
      {
        question: "Can I edit the Slovenian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a dialect word or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Slovenian?",
        answer:
          "All 32 caption styles apply to Slovenian. Preview the style on your clip to confirm č, š and ž render cleanly in its font.",
      },
      {
        question: "Does Crayo translate Slovenian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Slovenian lines into English, German or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CAPTION_STYLES],
  },
  spanish: {
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    flag: "ES",
    script: "Latin",
    regions: ["Mexico", "Spain", "Colombia", "Argentina", "United States"],
    seoTitle: "Spanish Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Spanish podcasts, streams and interviews with Crayo and export vertical clips with animated captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Spanish speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Spanish-speaking clippers and creators in Mexico, Spain, Colombia, Argentina and the United States who post captioned vertical clips to TikTok, Reels and Shorts.",
    body: [
      "Spanish is the second-largest language in short-form video. Mexico, Spain, Colombia and Argentina each have major podcast and streaming scenes, Spanish-language streamers hold some of the biggest Twitch records ever set, and Hispanic creators in the United States add another huge audience. Clips from those shows live on TikTok, Instagram Reels and YouTube Shorts with captions as standard.",
      "Spanish is written in the Latin alphabet with accents, the ñ and inverted question and exclamation marks at the start of a sentence. Accents distinguish meaning, so a missing one reads as an error, and regional vocabulary varies widely between Madrid, Mexico City and Buenos Aires. Crayo transcribes all varieties as Spanish when selected; names, slang and the inverted marks are the lines to check in the caption list.",
      "A practical tip: Spanish speakers talk fast and sentences run longer than English ones, so a caption style with a strong highlight on the active word and two to four words per caption keeps viewers in step with the speech. Preview the style on the clip to confirm ñ and accented vowels render cleanly in its font.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Spanish videos?",
        answer:
          "Yes. Spanish is in Crayo's language list and covers European and Latin American speech. Select it before running AutoClip.",
      },
      {
        question: "Can I edit the Spanish transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so accents, slang and names can be fixed in place with the timing kept.",
      },
      {
        question: "Which caption styles work for Spanish?",
        answer:
          "All 32 caption styles apply to Spanish. Highlight-driven word styles suit fast conversation; preview on your clip before exporting.",
      },
      {
        question: "Does Crayo translate Spanish captions?",
        answer:
          "Yes. The Translate button in the caption list converts Spanish lines into English, Portuguese or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, STREAMERS, CAPTION_STYLES],
  },
  swedish: {
    code: "sv",
    name: "Swedish",
    nativeName: "Svenska",
    flag: "SE",
    script: "Latin",
    regions: ["Sweden", "Finland (Swedish-speaking)"],
    seoTitle: "Swedish Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Swedish podcasts, streams and interviews and burns in animated captions on vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Swedish speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Swedish podcasters, streamers and clippers who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Sweden is a podcast powerhouse, with comedy, interview and true crime shows that record long weekly episodes, and Swedish streamers and esports players have a long history on Twitch and YouTube. Short clips reach a TikTok-heavy home audience and Swedish speakers in Finland, and Norwegian and Danish viewers can read Swedish captions well enough to follow.",
      "Swedish is written in the Latin alphabet with å, ä and ö, which sort at the end of the alphabet and are distinct letters rather than accented ones. Spoken Swedish is clear and transcribes well; the lines to check are compound nouns, which can get long, names and the English words that pepper Swedish tech and gaming talk. Crayo transcribes the speech as Swedish when selected.",
      "A practical tip: for a calm interview podcast a two-line sentence style reads naturally, but for fast comedy or gaming clips a word-by-word style keeps pace with the speaker. Either way, preview the style on the clip to confirm å, ä and ö are easy to tell apart at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Swedish videos?",
        answer:
          "Yes. Swedish is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Swedish rather than Norwegian or Danish.",
      },
      {
        question: "Can I edit the Swedish transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a compound or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Swedish?",
        answer:
          "All 32 caption styles apply to Swedish. Preview the style on your clip to confirm å, ä and ö render cleanly in its font.",
      },
      {
        question: "Does Crayo translate Swedish captions?",
        answer:
          "Yes. The Translate button in the caption list converts Swedish lines into English or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, STREAMERS, CAPTION_STYLES],
  },
  tamil: {
    code: "ta",
    name: "Tamil",
    nativeName: "தமிழ்",
    flag: "IN",
    script: "Tamil",
    regions: ["Tamil Nadu, India", "Sri Lanka", "Singapore", "Malaysia", "Tamil diaspora"],
    seoTitle: "Tamil Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Tamil podcasts, interviews and streams with Crayo and export vertical clips with animated Tamil captions for Shorts, Reels and TikTok.",
    answer:
      "Crayo transcribes Tamil speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Tamil creators in Tamil Nadu, Sri Lanka, Singapore, Malaysia and the diaspora who post captioned vertical clips to YouTube Shorts, Instagram Reels and TikTok.",
    body: [
      "Tamil has one of the most engaged audiences in Indian short-form video, with Chennai film interview channels, comedy podcasts, gaming streamers and political talk shows producing long recordings, plus large Tamil communities in Sri Lanka, Singapore, Malaysia and the West. YouTube Shorts leads, with Instagram Reels close behind, and TikTok matters outside India.",
      "Tamil is written in its own abugida script with rounded letters, vowel signs that attach before, after, above or below consonants, and a relatively small set of consonants compared to other Indian scripts. Tamil speakers mix in English constantly, and those words usually come through in Latin letters. Crayo transcribes the speech as Tamil when selected; names and English terms are the lines to check in the caption list.",
      "A practical tip: Tamil glyphs are wide, so give captions a large text size and two to three words per caption, and preview the style on the clip before export because the fonts differ across the 32 styles and the vowel signs need a clean rendering to stay readable on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Tamil videos?",
        answer:
          "Yes. Tamil is in Crayo's language list. Select it before running AutoClip so the speech is transcribed in Tamil script.",
      },
      {
        question: "Can I edit the Tamil transcript?",
        answer:
          "Yes. Every caption line is editable in the editor and the timing is unchanged after any edit.",
      },
      {
        question: "Which caption styles work for Tamil script?",
        answer:
          "All 32 caption styles can be applied to Tamil captions. Preview a few on the clip and keep the one whose font renders Tamil letters cleanly at phone size.",
      },
      {
        question: "Does Crayo translate Tamil captions?",
        answer:
          "Yes. The Translate button in the caption list converts Tamil lines into English or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, CAPTION_STYLES, PODCASTS],
  },
  telugu: {
    code: "te",
    name: "Telugu",
    nativeName: "తెలుగు",
    flag: "IN",
    script: "Telugu",
    regions: ["Andhra Pradesh and Telangana, India", "Telugu diaspora in the US"],
    seoTitle: "Telugu Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Telugu podcasts, interviews and streams and adds animated Telugu captions to vertical clips for YouTube Shorts, Instagram Reels and more.",
    answer:
      "Crayo transcribes Telugu speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Telugu creators in Andhra Pradesh, Telangana and the diaspora who post captioned vertical clips to YouTube Shorts and Instagram Reels.",
    body: [
      "Telugu is one of the biggest languages in Indian entertainment, with Hyderabad film interview channels, celebrity podcasts, comedy shows and gaming streamers producing hours of long-form content, and a large diaspora in the United States that follows along. YouTube Shorts and Instagram Reels are where clips get their views, and captions are essential for muted mobile viewing.",
      "Telugu is written in a rounded abugida script, with vowel signs attached to consonants and conjunct forms that stack, which makes captions dense and intricate. Speakers mix in English freely, especially in tech and film talk, and those words usually appear in Latin letters. Crayo transcribes the speech as Telugu when selected; names and English terms are the lines to check in the caption list.",
      "A practical tip: Telugu glyphs need size to stay legible, so use a large caption text, two to three words per caption, and preview the style on the clip before export because fonts differ across the 32 styles and the stacked conjuncts need a clean rendering on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Telugu videos?",
        answer:
          "Yes. Telugu is in Crayo's language list. Select it before running AutoClip so the speech is transcribed in Telugu script.",
      },
      {
        question: "Can I edit the Telugu transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a name or an English term can be adjusted with the timing preserved.",
      },
      {
        question: "Which caption styles work for Telugu script?",
        answer:
          "All 32 caption styles can be applied to Telugu captions. Preview a few on the clip and keep the one whose font renders the rounded glyphs cleanly.",
      },
      {
        question: "Does Crayo translate Telugu captions?",
        answer:
          "Yes. The Translate button in the caption list converts Telugu lines into English, Hindi or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, PODCASTS, CAPTION_STYLES, CLIPPERS],
  },
  thai: {
    code: "th",
    name: "Thai",
    nativeName: "ไทย",
    flag: "TH",
    script: "Thai",
    regions: ["Thailand"],
    seoTitle: "Thai Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Thai podcasts, streams and interviews with Crayo and export vertical clips with animated Thai captions for TikTok, Shorts and Reels.",
    answer:
      "Crayo transcribes Thai speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Thai creators, streamers and clippers who post captioned vertical clips to TikTok, YouTube Shorts and Instagram Reels.",
    body: [
      "Thailand is one of the most TikTok-active countries in the world, and Thai podcasts, talk shows and gaming streamers produce long recordings that clippers cut for TikTok, YouTube Shorts and Instagram Reels. Thai viewers are used to heavy on-screen text from variety shows, so captioned clips feel native rather than added.",
      "Thai is written in its own script with no spaces between words and no capital letters, with vowel signs placed above, below, before or after consonants and tone marks stacked above. Line breaks must fall at word boundaries that the script does not mark, so caption segmentation matters more than in most languages. Crayo transcribes the speech into Thai script when selected; names, English loanwords and particles are the lines to check in the caption list.",
      "A practical tip: Thai needs extra vertical room for the stacked vowel and tone marks, so choose a caption style with generous line height, keep each caption short, and preview it on the clip before export because the fonts differ across the 32 styles and the marks need a clean rendering at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Thai videos?",
        answer:
          "Yes. Thai is in Crayo's language list. Select it before running AutoClip so the speech is transcribed into Thai script.",
      },
      {
        question: "Can I edit the Thai transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a word boundary or a loanword can be adjusted without changing the timing.",
      },
      {
        question: "Which caption styles work for Thai script?",
        answer:
          "All 32 caption styles can be applied to Thai captions. Preview a few on the clip and keep the one whose font renders the stacked vowel and tone marks cleanly.",
      },
      {
        question: "Does Crayo translate Thai captions?",
        answer:
          "Yes. The Translate button in the caption list converts Thai lines into English or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, CLIPPERS],
  },
  turkish: {
    code: "tr",
    name: "Turkish",
    nativeName: "Türkçe",
    flag: "TR",
    script: "Latin",
    regions: ["Turkey", "Cyprus", "Turkish diaspora in Germany and the Netherlands"],
    seoTitle: "Turkish Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Turkish podcasts, streams and interviews and burns in animated captions on vertical clips for TikTok, Instagram Reels and YouTube Shorts.",
    answer:
      "Crayo transcribes Turkish speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Turkish podcasters, streamers and clippers in Turkey and the diaspora who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Turkey is one of the largest YouTube and Instagram markets in Europe and the Middle East, with interview podcasts, comedy shows and political talk recording long episodes, and Turkish streamers drawing some of the biggest Twitch audiences anywhere. Clips reach a TikTok-heavy home audience and millions of Turkish speakers in Germany and the Netherlands.",
      "Turkish is written in the Latin alphabet with ç, ğ, ı, İ, ö, ş and ü, and the dotless ı versus dotted i distinction is a frequent caption error because many fonts and keyboards get it wrong. Turkish is agglutinative, so words carry long chains of suffixes. Crayo transcribes the speech as Turkish when selected; names, suffixed foreign words and the dotless ı are the lines to check in the caption list.",
      "A practical tip: long suffixed words and small diacritics argue for a caption style with large, heavy text and two to three words per caption, and previewing the style on the clip before export shows whether its font distinguishes ı from i clearly at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Turkish videos?",
        answer:
          "Yes. Turkish is in Crayo's language list. Select it before running AutoClip for a Turkish transcript with the correct letters.",
      },
      {
        question: "Can I edit the Turkish transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a dotless ı or a suffixed name can be corrected with the timing unchanged.",
      },
      {
        question: "Which caption styles work for Turkish?",
        answer:
          "All 32 caption styles apply to Turkish. Preview the style on your clip to confirm ı, İ, ğ and ş render cleanly in its font.",
      },
      {
        question: "Does Crayo translate Turkish captions?",
        answer:
          "Yes. The Translate button in the caption list converts Turkish lines into English, German or another listed language and preserves each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, VIDEO_EDITOR, CAPTION_STYLES],
  },
  ukrainian: {
    code: "uk",
    name: "Ukrainian",
    nativeName: "Українська",
    flag: "UA",
    script: "Cyrillic",
    regions: ["Ukraine", "Ukrainian diaspora in Poland, Germany, Canada and the US"],
    seoTitle: "Ukrainian Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Ukrainian podcasts, streams and interviews with Crayo and export vertical clips with animated Cyrillic captions for TikTok, Reels and Shorts.",
    answer:
      "Crayo transcribes Ukrainian speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Ukrainian podcasters, streamers and clippers at home and across the diaspora who post captioned vertical clips to TikTok, Instagram Reels and YouTube Shorts.",
    body: [
      "Ukrainian-language media has grown enormously, with interview podcasts, news talk and comedy shows recording long episodes and a streamer community that has moved firmly to Ukrainian. Clips reach a TikTok and Instagram audience at home and millions of Ukrainians now living in Poland, Germany, Canada and the United States, where captions keep the content accessible on a muted commute.",
      "Ukrainian is written in Cyrillic with letters Russian does not use, such as і, ї, є and ґ, and without the Russian ы and ё. Speech may mix in Russian words, and auto detection can confuse the two languages, so pick Ukrainian explicitly in the language picker. Crayo transcribes the speech as Ukrainian when selected; names and borrowed words are the lines to check in the caption list.",
      "A practical tip: Cyrillic words are a little wider than Latin ones, so two to three words per caption keeps text large, and a caption style with a strong highlight on the active word helps viewers follow fast interview exchanges. Preview the style on the clip to see how its font renders Ukrainian letters before exporting.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Ukrainian videos?",
        answer:
          "Yes. Ukrainian is in Crayo's language list. Select it before running AutoClip so the speech is transcribed as Ukrainian rather than detected as Russian.",
      },
      {
        question: "Can I edit the Ukrainian transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a borrowed word or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Ukrainian Cyrillic?",
        answer:
          "All 32 caption styles can be applied. Preview a couple on the clip and keep the one whose font renders і, ї, є and ґ cleanly.",
      },
      {
        question: "Does Crayo translate Ukrainian captions?",
        answer:
          "Yes. The Translate button in the caption list converts Ukrainian lines into English, Polish or another listed language while keeping the original timing.",
      },
    ],
    related: [AUTO_CLIP, CLIPPERS, PODCASTS, CAPTION_STYLES],
  },
  urdu: {
    code: "ur",
    name: "Urdu",
    nativeName: "اردو",
    flag: "PK",
    script: "Perso-Arabic (Nastaliq)",
    regions: ["Pakistan", "India", "Urdu diaspora in the UK, the Gulf and North America"],
    rtl: true,
    seoTitle: "Urdu Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Crayo transcribes Urdu podcasts, interviews and streams and adds animated Urdu captions to vertical clips for TikTok, YouTube Shorts and Instagram Reels.",
    answer:
      "Crayo transcribes Urdu speech and adds animated captions to short clips from podcasts, interviews and streams. It is for Urdu creators in Pakistan, India and the diaspora who post captioned vertical clips to TikTok, YouTube Shorts and Instagram Reels.",
    body: [
      "Pakistan has one of the fastest-growing short-form audiences in the world, with TikTok and YouTube Shorts leading, and Urdu podcasts, political talk shows, comedy channels and cricket commentary producing long episodes every week. A large diaspora in the United Kingdom, the Gulf and North America follows the same clips, and captions help them land for viewers who understand spoken Urdu better than fast conversation.",
      "Urdu is written right to left in the Perso-Arabic script, traditionally set in the Nastaliq style, where letters join and hang diagonally, with short vowels usually left unwritten. Speakers mix in English constantly, and spoken Urdu and Hindi are close enough that auto detection can confuse them, so choose Urdu explicitly in the language picker. Crayo transcribes the speech into Urdu script when selected; names and English words are the lines to check in the caption list.",
      "A practical tip for Urdu clips: preview the caption style on the actual clip before export, since right-to-left cursive text looks different across the fonts the 32 styles use, and keep captions to a few words so the joined letter forms stay large and legible on a phone.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Urdu videos?",
        answer:
          "Yes. Urdu is in Crayo's language list. Select it before running AutoClip so the speech is transcribed into Urdu script rather than detected as Hindi.",
      },
      {
        question: "Can I edit the Urdu transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so an English word or a name can be corrected with the timing left unchanged.",
      },
      {
        question: "Which caption styles work for Urdu script?",
        answer:
          "All 32 caption styles can be applied to Urdu captions. Fonts differ between styles, so preview a few on the clip and keep the one where the right-to-left script reads cleanly.",
      },
      {
        question: "Does Crayo translate Urdu captions?",
        answer:
          "Yes. The Translate button in the caption list converts Urdu lines into English or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, AI_CAPTIONS, PODCASTS, CLIPPERS],
  },
  vietnamese: {
    code: "vi",
    name: "Vietnamese",
    nativeName: "Tiếng Việt",
    flag: "VN",
    script: "Latin (with tone marks)",
    regions: ["Vietnam", "Vietnamese diaspora in the US, Australia and Europe"],
    seoTitle: "Vietnamese Auto Captions and Subtitles for Shorts",
    seoDescription:
      "Transcribe Vietnamese podcasts, streams and interviews with Crayo and export vertical clips with animated captions for TikTok, Shorts and Reels.",
    answer:
      "Crayo transcribes Vietnamese speech and adds animated captions to short clips from podcasts, streams and interviews. It is for Vietnamese creators, streamers and clippers in Vietnam and the diaspora who post captioned vertical clips to TikTok, YouTube Shorts and Facebook Reels.",
    body: [
      "Vietnam is one of the largest TikTok and Facebook markets in Southeast Asia, with podcasts, talk shows and a massive gaming and streaming scene producing long recordings that clippers cut daily. The diaspora in the United States, Australia and France follows along, and captions carry clips to viewers who scroll muted on a phone.",
      "Vietnamese is written in the Latin alphabet with a dense system of diacritics: tone marks stacked on vowels that already carry their own marks, such as ấ, ở and ữ, plus the đ. Those marks change meaning entirely, so a transcript that drops them is unreadable to native viewers. Crayo transcribes the speech as Vietnamese when selected; northern and southern pronunciations are both transcribed, and names and English loanwords are the lines to check in the caption list.",
      "A practical tip: stacked tone marks need vertical room and size, so use a caption style with generous line height, large text and two to four words per caption, and preview it on the clip before export because the fonts differ across the 32 styles and the stacked marks need a clean rendering at phone size.",
    ],
    faqs: [
      {
        question: "Does Crayo caption Vietnamese videos?",
        answer:
          "Yes. Vietnamese is in Crayo's language list. Select it before running AutoClip for a Vietnamese transcript with full tone marks.",
      },
      {
        question: "Can I edit the Vietnamese transcript?",
        answer:
          "Yes. Every caption line is editable in the editor, so a tone mark or a name can be corrected without changing the timing.",
      },
      {
        question: "Which caption styles work for Vietnamese?",
        answer:
          "All 32 caption styles can be applied to Vietnamese captions. Preview a few on the clip and keep the one whose font renders the stacked tone marks cleanly.",
      },
      {
        question: "Does Crayo translate Vietnamese captions?",
        answer:
          "Yes. The Translate button in the caption list converts Vietnamese lines into English or another listed language while keeping each line's timing.",
      },
    ],
    related: [AUTO_CLIP, STREAMERS, CAPTION_STYLES, CLIPPERS],
  },
};

export const LANGUAGE_SLUGS = Object.keys(LANGUAGE_PAGES);

export const LANGUAGE_COUNT = LANGUAGE_SLUGS.length;
